/* eslint-disable react-hooks/rules-of-hooks */
// ^ eslint-plugin-react-hooks 1.7 (pinned by react-scripts) counts code
//   paths with plain Numbers; this component has enough conditional JSX
//   that the count overflows and every hook after ~line 900 is reported
//   as "called conditionally" although none is (verified by extracting
//   blocks: the report moves, the hooks don't). Fixed upstream in 2.x
//   (BigInt). All hooks here are top-level and unconditional.
import { usePreference } from "helpers/userPrefs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { refreshIssueLists } from "actions/issues";
import { Button, Input, InputNumber, Select, Tooltip, message, Skeleton, Modal, Form } from "antd";
import { listGanttItems } from "api/gantt/ganttApi";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { leafActivityOptions, leafOption, LEAF_SELECT_PROPS, PATH_SEP, NOT_LEAF_NOTE } from "helpers/schedule/leafActivityOptions";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  ShareAltOutlined,
  SaveOutlined,
  PlusOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CameraOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  AimOutlined,
  HighlightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  BorderOutlined,
  BuildOutlined,
  DownOutlined,
  UpOutlined,
  ExpandOutlined,
  VideoCameraOutlined,
  SendOutlined,
  EditOutlined,
} from "@ant-design/icons";
import moment from "moment";
import WebGlApp from "webgl";
import { url, tokenRef } from "api";
import { uploadIssueAttachments, getIssueTags, setIssueEstimate, setIssuesApprovedToResolve } from "api/issue/issueApi";
import { getDiscipline } from "api/discipline/getDiscipline";
import DisciplineContractorPicker from "components/Issue/DisciplineContractorPicker";
import { getParticipantsRoster } from "api/participants/participantsApi";
import IssueTool from "components/DocumentControl/IssueTool/IssueTool";
import Issue3DPins from "./components/Issue3DPins";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import LocationPicker from "components/Coordination/LocationPicker";
import {
  getIssueByShareToken,
  getChanges,
  addChange,
  updateIssue,
  resendIssueDeliverable,
  updateIssueStatus,
  saveIssueMarkup,
  setIssueSheet,
  deleteIssueImage,
  deleteIssueSheet,
  deleteIssueAttachment,
  setGuestToken,
  getGuestToken,
  getCandidateFixElements,
  submitIssue,
} from "api/issue/issueApi";
import store from "store";
import { issueFinished } from "actions/issues";
import GuestRegistrationModal from "./GuestRegistrationModal";
import ShareIssueModal from "./ShareIssueModal";
import GuestProfileNudge from "components/Auth/GuestProfileNudge";
import { IssueStatusSelect, IssuePrioritySelect, IssueDueDateSelect, IssueAddPartner, IssueProgressControl } from "components/Issue/IssueQuickActions";
import PeoplePicker from "components/PeoplePicker/PeoplePicker";
import { emailValue as recipientEmailValue } from "components/PeoplePicker/peoplePickerOptions";
import { IssueSourceBar, IssueAttachmentsStrip, RemindAssigneeButton, EvidenceList, ResolveEvidenceModal, mediaKindOf, uploadPastedFiles, storedKindOf, KIND_META } from "components/Issue/IssueSourceControls";
import MediaHistoryStrip from "./components/MediaHistoryStrip";
import PendingProposalsPanel from "components/Issue/PendingProposalsPanel";
import ImportedIssueDetails from "components/Issue/ImportedIssueDetails";
import IssueShopDrawingLink from "components/Issue/IssueShopDrawingLink";
import { matchOption } from "components/SearchSelect/searchSelect";
import { ExtraScopeChip } from "components/Coordination/ExtraScope";
import { resolveDocumentUrl } from "helpers/pdfPreview";
import usePermissions from "hooks/usePermissions";
import useProjectAccess from "hooks/useProjectAccess";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { T } from "./theme";
import { issueLabel } from "helpers/artifactPrefix";
import { getCommitDiff } from "api/commits/commitsApi";
import IssueModelCanvas, { activeIssueViewer } from "./components/IssueModelCanvas";
import ModelEditsDrawer from "components/Coordination/ModelEditsDrawer";
import { IssueDuplicateMergeButton, IssueMergedBanner } from "components/Issue/IssueDuplicateMerge";
import { IssueMoveToProjectButton } from "components/Issue/IssueMoveToProject";
import IssueExportMenu from "components/Issue/IssueExportMenu";
import {
  defaultSurfaceFor, availableSurfaces, rememberSurface, recallSurface,
  issueVideos, issueDocuments, issueHasModel,
} from "helpers/issueSurface";
import IssueDocumentPane from "./components/IssueDocumentPane";
import IssueCameraCapture from "./components/IssueCameraCapture";
import { modelSourceLabel } from "pages/Issue/modelSource";
import { usePartnerTrades } from "components/Partners/partnerTrades";
import { PartnerWithTrades, TradeWithPartners } from "components/Partners/PartnerTradeLabel";
import { appBarsBottom } from "components/Header/AppTopBars";
import { issueActivityIds } from "helpers/schedule/issueActivityIds";

/* ─────────────────────────────────────────────────────────────────────────
 *  Issue Resolution Page
 *  Layouts picked from issue.sourceType:
 *    V1  {2d, docControl}     → real 2D IssueTool + screenshot
 *    V2  {3d, modelViewer}    → screenshot primary when one exists, 3D
 *                               collapsed behind a toggle; 3D expands as
 *                               primary only when it's the sole source
 *    V3  {hybrid, both}       → 2D sheet primary + collapsed 3D + linked meta
 *  Screenshot / 2D are the primary starting points; 3D stays available but
 *  secondary (collapsed by default, mounted only when expanded).
 *  Shared: top bar, meta strip, Activity rail with comment + resolve.
 * ──────────────────────────────────────────────────────────────────────── */

// Palette tokens now live in ./theme (shared by the whole issue feature).

// Screenshot urls can be data URIs, absolute urls, or server-relative paths
// ("/uploads/issue-screenshots/…") — prefix the latter with the API origin.
const toImgSrc = (s) =>
  s && typeof s === "string" && s.startsWith("/") ? `${url}${s}` : s;

// The screenshots that prove the FIX, as opposed to the source image that
// shows where the issue was raised.
//
// While a resolution waits for review, the proof lives on the proposal, not
// on the issue: `pendingResolution` (built server-side, see
// back/utils/pendingResolution.js) carries it whatever channel it came from
// — the Revit / Navisworks plugin uploads one screenshot and stamps its url
// on every selected element, the web dialog fills screenshotUrls[]. Once
// approved the same images are on issue.resolution.evidence.
const CHANNEL_LABEL = { revit: "Revit", navisworks: "Navisworks", web: "Web" };

function resolutionScreenshots(issue) {
  const out = [];
  const seen = new Set();
  const push = (u) => {
    if (!u || typeof u !== "string" || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };
  const looksImage = (x) =>
    (x?.mime && /^image\//i.test(x.mime)) ||
    ["image", "screenshot"].includes(String(x?.kind || "").toLowerCase()) ||
    /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(String(x?.url || ""));

  for (const u of issue?.pendingResolution?.screenshots || []) push(u);
  for (const e of issue?.resolution?.evidence || []) if (looksImage(e)) push(e.url);
  for (const e of issue?.resolvedElements || []) push(e?.screenshotUrl);
  return out;
}

// ─── Small shared primitives ─────────────────────────────────────────────
function Pill({ kind = "neutral", children, size = "sm" }) {
  const pad = size === "sm" ? "2px 7px" : "3px 9px";
  const fs = size === "sm" ? 10 : 10.5;
  const map = {
    neutral: { bg: "#F3F4F5", bd: "#E8EAEC", fg: "#526470" },
    navy: { bg: T.wash, bd: "#D4E3EC", fg: T.navyD },
    added: { bg: T.addedBg, bd: "#CDEEC1", fg: T.added2 },
    removed: { bg: T.removedBg, bd: "#FAD6D6", fg: T.removed2 },
    orange: { bg: "#FFF2E9", bd: "#FFD9BF", fg: "#B84B0A" },
    amber: { bg: T.ambBg, bd: "#F1DFB1", fg: "#8A6112" },
  };
  const s = map[kind] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.fg,
      padding: pad, borderRadius: 999,
      fontSize: fs, fontWeight: 700, lineHeight: 1, letterSpacing: ".03em",
      textTransform: "uppercase",
    }}>{children}</span>
  );
}

function SourceTag({ type }) {
  if (type === "2d" || type === "docControl") {
    return <span style={{ background: T.wash, color: T.navyD, padding: "3px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700 }}>2D</span>;
  }
  if (type === "3d" || type === "modelViewer") {
    return <span style={{ background: "#F1E8FB", color: "#7A4BBF", padding: "3px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700 }}>3D</span>;
  }
  return <span style={{ background: "#FFF2E9", color: "#B84B0A", padding: "3px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: ".04em" }}>2D+3D</span>;
}

function StatusPill({ status }) {
  const map = {
    open: { bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6", label: "Open" },
    pending: { bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B", label: "Pending" },
    resolved: { bg: "#D1FAE5", fg: "#065F46", dot: "#10B981", label: "Resolved" },
    abandoned: { bg: "#F3F4F6", fg: "#374151", dot: "#6B7280", label: "Abandoned" },
  };
  const s = map[status] || map.open;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.fg, padding: "3px 8px",
      borderRadius: 999, fontSize: 10.5, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} /> {s.label}
    </span>
  );
}

function PriPill({ priority }) {
  const map = {
    urgent: { bg: "#FEE2E2", fg: "#B91C1C" },
    normal: { bg: "#DBEAFE", fg: "#1E40AF" },
    low: { bg: "#F3F4F6", fg: "#4B5563" },
  };
  const s = map[priority] || map.normal;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: "3px 8px", borderRadius: 3,
      fontSize: 10, fontWeight: 700, letterSpacing: ".04em",
      textTransform: "uppercase",
    }}>{priority || "normal"}</span>
  );
}

// Top-bar update controls for signed-in editors: status, priority, add
// another partner (responsible). Same components as the issues table.
// Top-bar update controls: STATUS · PRIORITY · DUE · PROGRESS · ASSIGNED TO (multi) · Remind assignee
// (only when the viewer is not the assignee) · Add partner. ASSIGNED TO moved
// up here from the bar above the primary pane (2026-08-18), which now holds
// the sheet / media source controls instead.
// `guest` = working through an emailed magic link (a partner's
// responsible, or a supervisor copied on the issue). They get the same
// controls, minus the two that genuinely need an account: the assignee
// picker reads the project's user list, and Remind is a jwt-only route.
// Everything else - status, priority, handing the issue to a colleague
// by name and email - is theirs to use, and the API has accepted it from
// a guest session all along; only this bar was hiding it.
function EditorTopControls({ show, issue, onChanged, me, guest = false, canMerge = false }) {
  if (!show || !issue) return null;
  const lbl = { fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" };
  return (
    <>
      <span style={lbl}>STATUS</span>
      <IssueStatusSelect issue={issue} onChanged={onChanged} />
      <span style={lbl}>PRIORITY</span>
      <IssuePrioritySelect issue={issue} onChanged={onChanged} />
      <span style={lbl}>DUE</span>
      <IssueDueDateSelect issue={issue} onChanged={onChanged} />
      <span style={lbl}>PROGRESS</span>
      <IssueProgressControl issue={issue} onChanged={onChanged} />
      {/* One picker for everyone on the issue - the create page's
          multi-select: tick to add (they start getting updates), untick
          to remove. The first person on an unassigned issue becomes the
          assignee. */}
      <span style={lbl}>ASSIGNED TO</span>
      <IssueAddPartner issue={issue} onChanged={onChanged} />
      {!guest && <RemindAssigneeButton issue={issue} me={me} onChanged={onChanged} />}
      {/* Owner side only: fold this report into the one it duplicates. */}
      {!guest && <IssueDuplicateMergeButton issue={issue} canMerge={canMerge} />}
      {/* Owner side only: raised on the wrong project - move it, and it
          takes that project's prefix and next number. */}
      {!guest && <IssueMoveToProjectButton issue={issue} canMove={canMerge} onMoved={onChanged} />}
      {/* This issue alone: BCF 2.1 or a one-issue PDF with its details,
          attachments and every comment. Signed-in users only (jwt route). */}
      {!guest && issue?._id && (
        <IssueExportMenu
          size="small"
          label="Export BCF / PDF / Excel / CSV"
          filters={{ ids: [String(issue._id)], projectId: issue?.projectId?._id || issue?.projectId || undefined }}
          tooltip="Export this issue as BCF 2.1 (.bcfzip) or as a PDF with its details, attachments and comments"
        />
      )}
    </>
  );
}

// IS THE BAR STUCK?
//
// A sentinel above the bar, watched by an IntersectionObserver: the
// moment it leaves the top of the viewport the bar is pinned. Cheaper
// and steadier than a scroll listener (no work per scroll event, no
// layout read), and it needs no knowledge of how tall anything above it
// is - which matters here because the breadcrumb row is only rendered
// for signed-in users.
function useStuck() {
  const sentinelRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    // The bar pins under the app's top block, so "stuck" is the sentinel
    // passing under THAT block, not the window edge (viewport px, as the
    // observer measures).
    const io = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: `-${Math.round(appBarsBottom())}px 0px 0px 0px` }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [sentinelRef, stuck];
}

// Why "Mark resolved" is disabled (tooltip), or undefined when it isn't.
function resolveBlockReason({ canResolve, hasScreenshot, hasTaggedUsers, needsCommit, hasCommit, status }) {
  if (canResolve) return undefined;
  const parts = [];
  if (!hasScreenshot) parts.push("needs a screenshot");
  if (!hasTaggedUsers) parts.push("needs a responsible person");
  if (needsCommit && !hasCommit) parts.push("needs a linked model commit");
  if (status === "resolved") parts.push("already resolved");
  return parts.join(" · ") || undefined;
}

// "Markup v3 by Sarah Chen · 5 minutes ago" for the meta strip.
function markupSummary(issue) {
  const m = issue?.markupLastEditedBy;
  if (!m || !m.at) return null;
  const versions = issue?.screenshotVersions || [];
  const version = m.version || versions.length || 1;
  return {
    version, name: m.name || m.email || "someone",
    when: moment(m.at).fromNow(),
    title: `${versions.length} image version(s) kept · last saved ${moment(m.at).format("YYYY-MM-DD HH:mm")}`,
  };
}

// "Name — email, Name — email +n" for the meta strip.
function assigneeSummary(list) {
  const parts = (list || []).slice(0, 2).map((a) => {
    const p = a && a.userId && typeof a.userId === "object" ? a.userId : a;
    const nm = [p?.name, p?.lastName].filter(Boolean).join(" ") || p?.email || "";
    return p?.email ? `${nm} — ${p.email}` : nm;
  });
  const extra = (list || []).length > 2 ? ` +${list.length - 2}` : "";
  return parts.join(", ") + extra;
}

function Avatar({ user, size = 28 }) {
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: user?.color || T.navy, color: "#fff",
      fontSize: Math.floor(size * 0.42), fontWeight: 600,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>{initial}</div>
  );
}

// The Document Control sheet behind the issue's active 2D sheet, for the
// markup tool's "Show grid" (the building grid on the page, not a lattice):
// the issue's sourceDocControlId, else the roster entry for the same URL. An
// imported screenshot with no register row gives null, and the tool keeps its
// drawing grid and says why.
export function sheetRefOfIssue(issue) {
  if (!issue || !issue.sourceDocument || !issue.sourceDocument.url) return null;
  const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";
  const projectId = idOf(issue.projectId);
  const entry = (issue.sourceDocuments || []).find((d) => d && d.url === issue.sourceDocument.url && d.docControlId);
  const docId = idOf(issue.sourceDocControlId) || idOf(entry && entry.docControlId);
  return projectId && docId ? { projectId: String(projectId), docId: String(docId) } : null;
}

// ─── Real 2D viewer (uses existing IssueTool from DocumentControl) ───────
function SheetPane({ issue, canEdit, userInfo, onSave, fill = true, height, saveRef, fullscreen }) {
  const rawUrl = issue?.sourceDocument?.url || null;
  const sheet = issue?.sourceDocument?.sheetNumber || "";
  const name = issue?.sourceDocument?.name || "Sheet";
  // Doc-control sheets are stored as "uploads/<id>.pdf.gz" (gzipped, on
  // R2): fetch + inflate into a blob URL for pdf.js; plain uploads and
  // images only need the API origin. Until resolved the pane shows a
  // loading note instead of a "please try again" canvas.
  const [documentUrl, setDocumentUrl] = useState(null);
  const [docErr, setDocErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setDocumentUrl(null); setDocErr(null);
    if (!rawUrl) return undefined;
    resolveDocumentUrl(rawUrl)
      .then((u) => { if (alive) setDocumentUrl(u || null); })
      .catch((e) => { if (alive) setDocErr(e.message || "Could not load the sheet"); });
    return () => { alive = false; };
  }, [rawUrl]);

  const shellStyle = {
    position: "relative",
    background: "#FBFBF7",
    border: `1px solid ${T.line}`,
    borderRadius: 6,
    overflow: "hidden",
    ...(fill ? { flex: 1, minHeight: 0, width: "100%" } : { height: height || 320 }),
  };

  if (!documentUrl) {
    return (
      <div style={{
        ...shellStyle,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: docErr ? "#c62828" : T.muted, fontSize: 12, textAlign: "center", padding: 16,
      }}>
        {!rawUrl
          ? "No 2D sheet attached to this issue - pick one in SHEET above, or import a PDF / image."
          : (docErr ? `Sheet could not be loaded: ${docErr}` : `Loading sheet ${sheet || name}…`)}
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={{ position: "absolute", inset: 0 }}>
        <IssueTool
          isOpen
          embedded
          hideHeader
          documentUrl={documentUrl}
          documentName={name || issue?.title}
          sheetRef={sheetRefOfIssue(issue)}
          userInfo={userInfo}
          onSaveIssue={onSave}
          saveRef={saveRef}
          fullscreen={fullscreen}
          initialIssueObjects={issue?.issueObjects}
          canEdit={canEdit}
        />
      </div>
      <div style={{
        position: "absolute", top: 8, left: 8,
        background: "#fff", border: `1px solid ${T.line}`, borderRadius: 3,
        padding: "4px 8px", fontSize: 10.5, fontWeight: 600, color: T.ink,
        zIndex: 5,
      }}>{sheet ? `${sheet} · ${name}` : name}</div>
    </div>
  );
}

// ─── RESOLUTION SCREENSHOT rail ──────────────────────────────────────────
//
// One component for all three layouts, because all three had the same bug:
// they rendered the issue's CURRENT image under a "RESOLUTION SCREENSHOT"
// heading and, when a resolution had been pushed from Revit / Navisworks
// with its screenshot attached, still showed "REQUIRED" - the plugin's
// image lives on the proposal until a reviewer approves it, and nothing
// here looked there.
//
// `shot` is the resolution's own screenshot; `fallback` is the issue image,
// which still satisfies the resolve requirement (that is what
// ResolveEvidenceModal records when nothing else is attached) but is
// labelled honestly rather than passed off as proof of the fix.
// Hours spent on the issue, under the resolution screenshot. The server
// sends `issue.hoursSpent` only to owner-company users (reviewers, admins,
// members) and adds the cost only for the project owner / company admins
// (back/utils/issueHours), so its absence means "not for you".
function ResolutionHours({ hours }) {
  if (!hours) return null;
  const fmt = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
  const latest = hours.latest || null;
  const bits = [];
  if (latest && (latest.companyName || latest.name)) bits.push(latest.companyName || latest.name);
  if ("cost" in hours && hours.entered) {
    bits.push(hours.cost == null ? "no rate set" : `$${Number(hours.cost).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${hours.costComplete === false ? " (some hours unrated)" : ""}`);
  }
  if (latest && latest.status === "pending") bits.push("awaiting review");
  if (hours.submissions > 1) bits.push(`${hours.submissions} submissions`);
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>HOURS SPENT</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
        {hours.entered ? `${fmt(hours.hours)} h` : "Not entered"}
      </span>
      {bits.length > 0 && <span style={{ fontSize: 10.5, color: T.muted }}>{bits.join(" · ")}</span>}
    </div>
  );
}

function ResolutionShotBox({ shot, fallback, info, hours, dark = false, maxHeight = 200, onOpen }) {
  const src = shot || fallback;
  const fromPlugin = !!shot && !!info?.channel && info.channel !== "web";
  const pill = shot
    ? <Pill kind="added">✓ {info?.channel ? `FROM ${(CHANNEL_LABEL[info.channel] || info.channel).toUpperCase()}` : "CAPTURED"}</Pill>
    : (fallback ? <Pill kind="navy">ISSUE IMAGE</Pill> : <Pill kind="orange">REQUIRED</Pill>);
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 8px" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
          RESOLUTION SCREENSHOT
        </span>
        {pill}
      </div>
      <div style={{
        position: "relative", minHeight: dark ? 120 : 160, maxHeight,
        background: dark ? "#1A1A1A" : "#FDFAF1",
        border: `1px solid ${dark ? "#0C0C0C" : T.line}`, borderRadius: 6, overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: src && onOpen ? "zoom-in" : "default",
      }}
        onClick={() => { if (src && onOpen) onOpen(src); }}
        title={src && onOpen ? "Show this on the canvas" : undefined}
      >
        {src ? (
          <img src={toImgSrc(src)} alt="resolution" style={{ maxWidth: "100%", maxHeight: maxHeight - 4, objectFit: "contain" }} />
        ) : (
          <div style={{
            fontSize: 11.5, color: dark ? "rgba(255,255,255,.6)" : T.muted,
            padding: 20, textAlign: "center",
          }}>
            <CameraOutlined /><br />No screenshot yet.
          </div>
        )}
      </div>
      {src && (
        <div style={{ fontSize: 10.5, color: T.muted, marginTop: 5, lineHeight: 1.35 }}>
          {shot
            ? <>
                {fromPlugin ? `Pushed from ${CHANNEL_LABEL[info.channel] || info.channel}` : "Attached with the resolution"}
                {info?.proposer?.name ? ` by ${info.proposer.name}` : ""}
                {info?.submittedAt ? ` · ${moment(info.submittedAt).format("MMM D, HH:mm")}` : ""}
                {info?.status === "pending" ? " · awaiting review" : ""}
              </>
            : "The issue's current image — no resolution screenshot pushed yet."}
        </div>
      )}
      <ResolutionHours hours={hours} />
    </>
  );
}

// ─── Screenshot markup pane ──────────────────────────────────────────────
// Same IssueTool markup surface as SheetPane, but over the issue's raster
// screenshot instead of a 2D sheet. The pane always shows the LATEST
// annotated raster (sourceModelViewer.screenshot) as its base document —
// each save bakes prior markup in, so no initialIssueObjects are seeded
// here (they'd double-render on top of the baked pixels).
function ScreenshotMarkupPane({ issue, canEdit, userInfo, onSave, screenshot, fill = true, height, saveRef, fullscreen }) {
  const src = toImgSrc(screenshot);
  const shellStyle = {
    position: "relative",
    background: "#FBFBF7",
    border: `1px solid ${T.line}`,
    borderRadius: 6,
    overflow: "hidden",
    ...(fill ? { flex: 1, minHeight: 0, width: "100%" } : { height: height || 320 }),
  };
  if (!src) {
    return (
      <div style={{
        ...shellStyle,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: T.muted, fontSize: 12,
      }}>
        No screenshot attached to this issue.
      </div>
    );
  }
  return (
    <div style={shellStyle}>
      <div style={{ position: "absolute", inset: 0 }}>
        <IssueTool
          isOpen
          embedded
          hideHeader
          fitOnLoad
          documentUrl={src}
          documentName={issue?.title || "Issue screenshot"}
          userInfo={userInfo}
          onSaveIssue={onSave}
          saveRef={saveRef}
          fullscreen={fullscreen}
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}


// Action bar (STATUS / PRIORITY / ASSIGNED TO) lives in
// components/Issue/IssueQuickActions so the issues list shares it.
// ─── Real 3D viewer (uses existing WebGlApp from /webgl) ─────────────────
// (The "set a password" nudge for magic-link accounts is app-wide now:
// components/Auth/SetPasswordNudge.jsx, mounted in App.jsx.)
// Status control for NO-ACCOUNT guests (magic-link recipients). Guests
// can't file proposals (those need an account), so "Resolved" is set
// directly through PUT /issue/:id/status — the owner still closes.
const GUEST_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "under_review", label: "In review" },
  { value: "resolved", label: "Resolved" },
];
function GuestActionBar({ issue, guest, onChanged }) {
  const [saving, setSaving] = useState(false);
  const statusValue = issue?.status;
  const options = GUEST_STATUS_OPTIONS.some((o) => o.value === statusValue)
    ? GUEST_STATUS_OPTIONS
    : [...GUEST_STATUS_OPTIONS, { value: statusValue, label: String(statusValue || "").replace(/_/g, " "), disabled: true }];
  const handleStatus = async (value) => {
    setSaving(true);
    try {
      const r = await updateIssueStatus(issue._id, value);
      if (r && r.pendingReview) message.success(r.message || "Sent to the project owner's Review Center - it takes effect once approved");
      else message.success("Status updated — the owner has been notified");
      await onChanged?.();
    } catch (e) {
      message.error(e.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      padding: "8px 12px", background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6, marginBottom: 8,
    }}>
      <span style={{ fontSize: 12, color: T.muted }}>
        Working as <b style={{ color: T.ink }}>{guest?.name || guest?.email}</b>{guest?.company ? ` · ${guest.company}` : ""}
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 12, color: T.muted }}>Status</span>
      <Select
        size="small"
        style={{ minWidth: 150 }}
        value={statusValue}
        options={options}
        loading={saving}
        disabled={saving}
        onChange={handleStatus}
      />
    </div>
  );
}

// ─── Collapsed 3D section (secondary source) ─────────────────────────────
// The WebGL canvas is only MOUNTED once the user expands it — a WebGL
// context and a streaming session are not things to spin up for an issue
// nobody has asked to see in 3D.
function Collapsible3D({
  issue, canEdit, userInfo, onIssueUpdated, fileLabel, height = 340, review = null,
}) {
  const [expanded, setExpanded] = useState(false);
  // Starting a 3D change review opens the pane - the button that starts
  // it lives in the rail, and a review nobody can see is not a review.
  useEffect(() => { if (review?.active) setExpanded(true); }, [review?.active]);
  // A resolution push links the manifest without a zip listing, so ask
  // the shared rule rather than the zip.
  if (!issueHasModel(issue)) return null; // nothing to show — hide the toggle
  return (
    <div style={{ marginTop: 10, flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Button
          size="small"
          icon={<BuildOutlined />}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <>Hide 3D model <UpOutlined style={{ fontSize: 10 }} /></>
            : <>Show 3D model <DownOutlined style={{ fontSize: 10 }} /></>}
        </Button>
        <span style={{ fontSize: 11, color: T.muted }}>
          3D model (secondary) — element selections stream in from the
          Revit / Navisworks plugins when connected.
        </span>
      </div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <IssueModelCanvas
            issue={issue} canEdit={canEdit} userInfo={userInfo}
            onIssueUpdated={onIssueUpdated}
            fill={false} height={height}
            review={review}
          />
        </div>
      )}
    </div>
  );
}

// ─── 3D CHANGE (the resolution's model diff) ─────────────────────────────
//
// What the fixer actually changed in the model, read from the commit the
// plugin pushed with the resolution. Until now the reviewer had numbers
// nowhere and elements nowhere: the commit existed, its diff existed, and
// no issue surface read either.
//
// `tiles` is the count of streaming tiles the change touches - the same
// set the viewer prefetches - so it doubles as "is there anything to see
// in 3D at all".
/**
 * APPROVED TO RESOLVE - the green / red circle, and the switch for the people
 * allowed to flip it (owner 2026-10-26).
 *
 * ADVISORY. It blocks nothing: an unapproved issue can still be edited and
 * resolved, and whoever raised it can still edit it. The first spec blocked
 * both; on 686 live issues a flag defaulting to false would have locked every
 * one of them on deploy, and locking an issue against its own author means they
 * cannot fix their own typo.
 *
 * EVERYONE SEES THE STATE, ONLY A REVIEWER CHANGES IT. Somebody without the
 * right gets the circle and the words with no switch - the signal is for the
 * assignee, so hiding it from them would defeat the point.
 *
 * NO MAIL IS SENT FROM HERE. The server marks the issue and one mail per person
 * goes out after a settle period listing everything they are cleared on, so
 * twenty approvals are not twenty mails.
 */
function ApprovedToResolvePill({ issue, canApprove, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const on = !!issue?.approvedToResolve;
  const dot = (
    <span style={{
      display: "inline-block", width: 9, height: 9, borderRadius: "50%",
      background: on ? "#12b76a" : "#f04438", marginRight: 6,
    }} />
  );
  const words = on ? "Approved to resolve" : "Not approved";
  if (!canApprove) {
    return (
      <Tooltip title={on
        ? "A reviewer has cleared this issue to be worked on"
        : "No reviewer has cleared this issue yet - it can still be edited and resolved"}>
        <span style={{ fontSize: 11, color: on ? "#027948" : "#b42318", display: "inline-flex", alignItems: "center" }}>
          {dot}{words}
        </span>
      </Tooltip>
    );
  }
  const flip = async () => {
    setBusy(true);
    try {
      await setIssuesApprovedToResolve([String(issue._id)], !on);
      message.success(!on
        ? "Approved to resolve - the assignees are told shortly, in one email"
        : "Approval withdrawn");
      if (onChanged) onChanged();
    } catch (e) {
      message.error(e.message || "Could not change the approval");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Tooltip title={on
      ? "Cleared to be worked on. Click to withdraw."
      : "Click to clear this issue to be worked on. The assignees are emailed, once, for everything approved together."}>
      <button
        type="button"
        onClick={flip}
        disabled={busy}
        data-testid="approved-to-resolve-toggle"
        aria-pressed={on}
        style={{
          display: "inline-flex", alignItems: "center", cursor: busy ? "wait" : "pointer",
          fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 3,
          border: `1px solid ${on ? "#abefc6" : "#fecdca"}`,
          background: on ? "#ecfdf3" : "#fef3f2",
          color: on ? "#027948" : "#b42318",
        }}
      >
        {dot}{words}
      </button>
    </Tooltip>
  );
}

function ResolutionDiffStrip({ diff, loading, onReview, onEdits, reviewing, canReview }) {
  if (loading) {
    return (
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
        Loading the model change…
      </div>
    );
  }
  if (!diff) return null;
  const s = diff.summary || {};
  const tiles = new Set();
  Object.values(diff.changedTilesByCategory || {}).forEach((list) =>
    (list || []).forEach((t) => { if (t) tiles.add(t); })
  );
  const nothing = !s.added && !s.modified && !s.removed;
  return (
    <div style={{
      border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 10px",
      marginBottom: 8, background: "#fff",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
          3D CHANGE
        </span>
        {diff.scope === "selection" && (
          <Tooltip title="This push carried only the elements the fixer selected, so it says nothing about the rest of the model - deletions cannot be reported from it.">
            <span><Pill kind="navy">SELECTION</Pill></span>
          </Tooltip>
        )}
      </div>
      {nothing ? (
        <div style={{ fontSize: 11.5, color: T.muted }}>
          The pushed commit carries no element changes.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.added2 }}>
              +{s.added || 0}<span style={{ fontSize: 10.5, fontWeight: 500, color: T.muted }}> added</span>
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.amber }}>
              ~{s.modified || 0}<span style={{ fontSize: 10.5, fontWeight: 500, color: T.muted }}> modified</span>
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.removed2 }}>
              −{s.removed || 0}<span style={{ fontSize: 10.5, fontWeight: 500, color: T.muted }}> removed</span>
            </span>
            <span style={{ fontSize: 11, color: T.muted }}>{tiles.size} tile{tiles.size === 1 ? "" : "s"}</span>
          </div>
          <Button
            size="small"
            type={reviewing ? "default" : "primary"}
            icon={<BuildOutlined />}
            style={{ marginTop: 8, width: "100%" }}
            onClick={onReview}
            disabled={tiles.size === 0 && !(diff.elements || []).length}
            title={canReview
              ? "Show the changed elements in the 3D pane - the rest of the model drops back so the change reads on its own"
              : "Show the changed elements in the 3D pane"}
          >
            {reviewing ? "Hide the 3D change" : "View 3D change"}
          </Button>
          {/* The change as geometry, both generations in one scene with a
              slider between them - the same review /analytics/models
              gives a push, here for the elements this resolution touched. */}
          <Button
            size="small"
            style={{ marginTop: 6, width: "100%" }}
            onClick={onEdits}
            disabled={!(diff.elements || []).length}
            title="Old and new geometry of the changed elements in one scene, with a slider between the two generations"
          >
            Show model edits (old / new)
          </Button>
        </>
      )}
    </div>
  );
}

// ─── Resolve checklist ───────────────────────────────────────────────────
function ResolveChecklist({ hasScreenshot, hasTaggedUsers, hasCommit, needsCommit, policy, hasDocument }) {
  const row = (ok, label, hint) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0" }}>
      <span style={{
        width: 18, height: 18, borderRadius: "50%",
        background: ok ? T.added : T.line2,
        color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
      }}>{ok ? "✓" : "!"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: ok ? T.ink : "#B84B0A" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, color: T.muted,
        letterSpacing: ".06em", marginBottom: 4,
      }}>
        REQUIRED TO RESOLVE
      </div>
      {(!policy || policy.screenshot !== false) && row(hasScreenshot, "Screenshot attached", "Screenshot of the progress - can be added in the Mark resolved dialog.")}
      {row(hasTaggedUsers, "At least one user tagged", "Confirms who signed off on the change.")}
      {(needsCommit || (policy && policy.modelPush)) && row(hasCommit, "Linked model commit", policy && policy.modelPush ? "This issue requires a model push / diff from Revit or Navisworks." : "3D-linked resolutions must reference a diff.")}
      {policy && policy.document && row(!!hasDocument, "Document uploaded", "PDF / Excel / CSV sheet - can be added in the Mark resolved dialog.")}
      {policy && policy.pluginResolve && row(false, "Resolve pushed from the plugin", "Only the Revit / Navisworks plugin can resolve this issue.")}
    </div>
  );
}

// ─── Activity thread ─────────────────────────────────────────────────────
//
// TWO WEIGHTS, NOT ONE.
//
// What a person wrote is the reason anyone opens this panel; "Markup
// saved - image v7" and "Sheet set to A-201" are the paper trail around
// it. Rendered identically, the bookkeeping outnumbers the conversation
// and buries it. So a human comment is a full card at reading size, and
// everything the system logged for itself is a half-tone one-liner
// beside a small dot - still there, still in order, no longer competing.
//
// The one exception is a milestone (opened / marked resolved): it keeps
// its coloured pill so the two moments that bracket the issue stay
// findable in a long trail.
function ActivityThread({ events, compact }) {
  if (!events?.length) {
    return <div style={{ fontSize: 12, color: T.sub, fontStyle: "italic" }}>No activity yet.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 6 : 8 }}>
      {events.map((e, i) => {
        const isResolved = e.kind === "resolved" || e.changeType === "resolved";
        const isOpened = e.kind === "opened" || (i === events.length - 1 && e.changeType === "add");
        // Written by a person, in their own words - as opposed to a line
        // a route pushed onto changeHistory to record what it just did.
        const isComment = e.changeType === "comment";
        const milestone = isResolved || isOpened;
        const when = e.timestamp ? moment(e.timestamp) : null;

        if (isComment) {
          return (
            <div key={e._id || i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Avatar user={e.who} size={30} />
              <div style={{
                flex: 1, minWidth: 0,
                background: "#fff",
                border: `1px solid ${T.line2}`,
                borderLeft: `3px solid ${T.navy}`,
                borderRadius: 6, padding: "10px 12px",
                boxShadow: "0 1px 2px rgba(35,52,62,.06)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink }}>{e.who?.name || "Unknown"}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 10.5, color: T.muted }} title={when ? when.format("MMM D, YYYY HH:mm") : ""}>
                    {when ? when.format("MMM D, HH:mm") : ""}
                  </span>
                </div>
                <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {e.text || e.description || ""}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={e._id || i} style={{
            display: "flex", gap: 8, alignItems: "flex-start",
            paddingLeft: 8, opacity: milestone ? 0.9 : 0.62,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%", flexShrink: 0, marginTop: 6,
              background: isResolved ? T.added : isOpened ? T.orange : T.line2,
            }} />
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
              {milestone && (isResolved
                ? <Pill kind="added">✓ marked resolved</Pill>
                : <Pill kind="orange">opened</Pill>)}
              <span style={{ fontSize: 10.5, color: T.text, lineHeight: 1.4, wordBreak: "break-word" }}>
                {e.text || e.description || ""}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 9.5, color: T.sub, whiteSpace: "nowrap" }} title={when ? when.format("MMM D, YYYY HH:mm") : ""}>
                {when ? when.format("MMM D") : ""}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────
// RESIZABLE COLUMNS. Defaults are the old fixed 300 / 340 px; each rail
// stays between 200 and 720 px so the canvas keeps room to work.
const COL_DEFAULT = { mid: 300, right: 340 };
const COL_MIN = 200;
const COL_MAX = 720;
function clampCols(w) {
  const c = (v, d) => Math.max(COL_MIN, Math.min(COL_MAX, Math.round(Number(v) || d)));
  return { mid: c(w && w.mid, COL_DEFAULT.mid), right: c(w && w.right, COL_DEFAULT.right) };
}

// RESEND DELIVERABLES in front of each saved deliverable recipient (owner,
// 2026-10-30): mails that one person now, the deliverables attached - the
// approval mail only ever goes once. The server refuses when nothing has been
// delivered; `hasDeliverables` only greys the button early.
export function RecipientResendList({ issue, hasDeliverables, onSent }) {
  const [resending, setResending] = useState(""); // the address being sent to
  const rows = (issue && issue.recipients ? issue.recipients : [])
    .map((r) => ({ ...r, email: String(r.email || "").toLowerCase() }))
    .filter((r) => r.email);
  if (!rows.length) return null;
  const send = async (r) => {
    setResending(r.email);
    try {
      await resendIssueDeliverable(issue._id, r.email);
      message.success(`Deliverables sent to ${r.name || r.email}`);
      if (onSent) onSent();
    } catch (e) {
      message.error(e.message || "Could not send the deliverables");
    } finally {
      setResending("");
    }
  };
  return (
    <div data-testid="issue-recipient-resend" style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 10px 8px" }}>
      {rows.map((r) => {
        const last = (issue.deliverableSends || []).filter((x) => x.email === r.email).slice(-1)[0];
        return (
          <div key={r.email} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <Tooltip title={hasDeliverables ? `Email ${r.email} the deliverables now, attached` : "Nothing has been delivered on this issue yet"}>
              <Button size="small" disabled={!hasDeliverables} loading={resending === r.email} onClick={() => send(r)}>
                Resend deliverables
              </Button>
            </Tooltip>
            <span style={{ color: T.text }}>{r.name ? `${r.name} · ${r.email}` : r.email}</span>
            {last ? (
              <span style={{ color: T.sub }}>
                sent {moment(last.at).fromNow()}{last.byName ? ` by ${last.byName}` : ""}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default function IssueResolutionPage() {
  const { shareToken } = useParams();
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user?.userInfo);
  const dispatch = useDispatch();
  // Sharing (email link / copy link / collaborators / public access) is
  // its own CRUD key - issueShare (partner employees don't hold it).
  const { has: hasPerm, isAdmin } = usePermissions();
  const canShare = hasPerm(ALL_PERMISSIONS.ISSUE_SHARE);
  // Reviewing a pending proposal from this page. The server is the real
  // gate (owner-company membership + reviewCenterApprove / the legacy
  // issueApproveResolution); this only decides whether to offer the
  // buttons.
  const canReviewProposals = !!isAdmin
    || hasPerm(ALL_PERMISSIONS.ISSUE_APPROVE_RESOLUTION)
    || hasPerm(ALL_PERMISSIONS.REVIEW_CENTER_APPROVE);
  // Login JWT payloads carry `id`; profile-shaped objects carry `_id`.
  const currentUserId = userInfoRedux ? (userInfoRedux._id || userInfoRedux.id) : undefined;

  const [issue, setIssue] = useState(null);
  const issueRef = useRef(null);
  useEffect(() => { issueRef.current = issue; }, [issue]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Bolt-on 2 — server-detected candidate elements. Non-null when the
  // issue has an openedAtSnapshot AND the project has commits after it.
  // Structure mirrors GET /issue/:id/candidate-fix-elements (added,
  // removed, modified, affectedTiles, ...). Prefill for resolvedElements
  // + input to viewer.stateStore.isolate().
  const [candidateFix, setCandidateFix] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [permission, setPermission] = useState("view");
  // Closed by the reviewer (Review Center): the page is read-only for
  // everyone but owner-side staff who can reopen.
  const [lockedClosed, setLockedClosed] = useState(false);
  // Image version being viewed (null = latest, editable).
  const [viewVersion, setViewVersion] = useState(null);
  // Full-screen markup mode: the body grid becomes a fixed overlay with the
  // canvas + the activity/comments rail; the panes stay mounted (no lost
  // markup). Esc exits.
  const [fullscreen, setFullscreen] = useState(false);
  // Column widths, remembered per person (ColumnHandle below the grid).
  const [colPref, setColPref] = usePreference("issue.columnWidths", COL_DEFAULT);
  const colW = clampCols(colPref || COL_DEFAULT);
  const setCols = useCallback((w) => {
    setColPref(clampCols(w));
    // The canvas, the 3D view and the sheet fit themselves on a window
    // resize; a column resize is one too, as far as they are concerned.
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  }, [setColPref]);
  const resetCols = useCallback(() => setCols(COL_DEFAULT), [setCols]);
  const [showShare, setShowShare] = useState(false);
  const fullscreenCtl = useMemo(() => ({ active: fullscreen, toggle: () => setFullscreen((f) => !f) }), [fullscreen]);
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);
  // No-account (guest) session: set by the ?g= magic link exchange or the
  // share-link registration modal. `guestReady` gates loading until the
  // ?g= swap has completed so the first fetch already carries the token.
  const [guest, setGuest] = useState(null);
  const [needsGuestInfo, setNeedsGuestInfo] = useState(false);
  const [guestReady, setGuestReady] = useState(() => {
    try { return !new URLSearchParams(window.location.search).get("g"); } catch { return true; }
  });
  const isGuest = !currentUserId && !!getGuestToken();

  // (?ml= magic-login links are exchanged app-wide in App.jsx before any
  // route renders; nothing to do here.)
  const magicPending = false;
  useEffect(() => {
    let g = "";
    try { g = new URLSearchParams(window.location.search).get("g") || ""; } catch { g = ""; }
    if (!g) return;
    (async () => {
      try {
        const resp = await fetch(`${url}/issue/guest/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ guestToken: g }),
        });
        const body = await resp.json().catch(() => ({}));
        if (resp.ok && body.token) {
          setGuestToken(body.token);
          setGuest(body.guest || null);
        } else {
          message.error(body.err || "This link could not be opened");
        }
      } catch (e) {
        message.error(e.message || "This link could not be opened");
      } finally {
        // Drop ?g= from the address bar (the token is now in session storage).
        try { history.replace(window.location.pathname); } catch { /* ignore */ }
        setGuestReady(true);
      }
    })();
  }, []); // eslint-disable-line

  // `silent` = background refresh (focus / poll / after an inline
  // action): keep the page mounted, just swap the data — never flash the
  // skeleton (that looked like the page reloading itself).
  const loadAll = useCallback(async (opts = {}) => {
    const silent = opts === true || opts?.silent === true || !!issueRef.current;
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const res = await getIssueByShareToken(shareToken);
      const md = res.data || res;
      // Background poll with nothing new → leave state (and the markup
      // canvases, which re-init on a new issue object) untouched.
      const prev = issueRef.current;
      const unchanged = silent && prev && prev._id === md._id &&
        String(prev.updatedAt) === String(md.updatedAt) &&
        prev.status === md.status && prev.priority === md.priority &&
        (prev.responsibleUsers || []).length === (md.responsibleUsers || []).length &&
        (prev.issueObjects || []).length === (md.issueObjects || []).length &&
        (prev.changeHistory || []).length === (md.changeHistory || []).length;
      if (unchanged) return;
      setIssue(md);
      setPermission(res.permission || md.permission || "view");
      setLockedClosed(!!res.lockedClosed);
      // Anonymous visitor on a public share link: identify (name/email)
      // before working — same modal the viewer uses.
      setNeedsGuestInfo(!!res.requiresGuestInfo && !res.isAuthenticated && !res.isGuest);
      if (res.isGuest && !guest) {
        const me = (md.guestCollaborators || []).map((g) => g.guestId).find((g) => g && g.email);
        if (me) setGuest({ name: me.name, email: me.email, company: me.company });
      }
      try {
        const ch = await getChanges(md._id, { limit: 100 });
        setChanges(ch?.changes || []);
      } catch { /* non-fatal */ }
    } catch (e) {
      setErr(e.message || "Failed to load issue");
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => { if (shareToken && guestReady && !magicPending) loadAll(); }, [shareToken, guestReady, magicPending, loadAll]);

  // Bolt-on 2 — auto-detect candidate fix elements once the issue
  // loads. Runs against the currently-materialized viewer if one is
  // mounted; the isolate + prefetch hint kicks in only when the 3D
  // tab is open. Cheap on the server (bounded by snapshot size), so
  // safe to run unconditionally even for non-3D issues (returns
  // reason=no-snapshot and we no-op).
  useEffect(() => {
    // Owner-side reads (jwt-only routes). A partner on a magic link would
    // just collect 401s in their console for features their session is
    // not entitled to.
    if (!issue?._id || isGuest) return;
    let cancelled = false;
    (async () => {
      const resp = await getCandidateFixElements(issue._id);
      if (cancelled) return;
      setCandidateFix(resp);
      if (!resp || resp.candidateCount === 0) return;
      try {
        const uids = [
          ...(resp.modified || []).map((e) => e.uniqueId),
          ...(resp.added    || []).map((e) => e.uniqueId),
        ].filter(Boolean);
        // Highlight-by-isolation. The viewer's streamingController
        // will prioritise loading affected tiles by virtue of the
        // isolate() call (only isolated uids' tiles stay renderable).
        // Streaming viewer state store: viewer.streaming.state (see
        // webgl/js/loader/streaming/streamingLoader.js); the pane that owns
        // the WebGlApp registers it in activeIssueViewer.
        const v = activeIssueViewer.current?.viewer;
        const store = v?.streaming?.state || v?.stateStore;
        if (store?.isolate && uids.length > 0) {
          store.isolate(uids);
        }
        // Prefetch hint: float the tiles that carry the candidates
        // to the front of the streaming controller's dispatch queue.
        // Coverage/hysteresis/LOD gates still decide *whether* a
        // tile loads; this only affects load ORDER.
        const affectedTiles = Array.isArray(resp.affectedTiles) ? resp.affectedTiles : [];
        if (affectedTiles.length > 0 && typeof v?.streaming?.prefetchTiles === "function") {
          v.streaming.prefetchTiles(affectedTiles);
        }
      } catch (e) {
        console.warn("[IssueResolutionPage] candidate isolate skipped:", e?.message);
      }
    })();
    return () => {
      cancelled = true;
      // Clear the prefetch hint when the effect tears down so a
      // subsequent issue's candidates don't inherit the previous
      // one's priority set.
      try {
        // (plain call form: CRA's lint rejects an optional-call as a bare expression)
        const st = activeIssueViewer.current?.viewer?.streaming;
        if (st && typeof st.clearPrefetch === "function") st.clearPrefetch();
      } catch (e) { /* best-effort */ }
    };
  }, [issue?._id]);

  // Partners updating through their emailed links write to this same issue —
  // pick their comments/screenshots/status changes up when the tab regains
  // focus, plus a slow background poll while it stays open.
  useEffect(() => {
    if (!shareToken) return;
    const onFocus = () => loadAll();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") loadAll();
    }, 45000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(timer); };
  }, [shareToken, loadAll]);

  const has2D = !!(issue?.sourceDocument && issue.sourceDocument.url);
  const has3D = !!(
    (issue?.sourceModel?.zipInfo?.paths && issue.sourceModel.zipInfo.paths.length) ||
    (issue?.sourceModelZipInfo?.paths && issue.sourceModelZipInfo.paths.length) ||
    issue?.sourceModelPath ||
    issue?.sourceModel?.path
  );

  // Normalize the source-type tag shown in chips. Fall back to what's attached.
  const sourceType = useMemo(() => {
    const raw = issue?.sourceType;
    if (raw === "hybrid" || raw === "both") return "hybrid";
    if (raw === "2d" || raw === "docControl") return "2d";
    if (raw === "3d" || raw === "modelViewer") return "3d";
    if (has2D && has3D) return "hybrid";
    if (has2D) return "2d";
    if (has3D) return "3d";
    return raw || "hybrid";
  }, [issue?.sourceType, has2D, has3D]);

  // Layout is driven by what's actually attached, not by the stored enum —
  // the enum has drifted across versions (2d/docControl, 3d/modelViewer,
  // hybrid/both) so derive from data.
  const layout = useMemo(() => {
    if (has2D && has3D) return "V3";
    if (has3D) return "V2";
    if (has2D) return "V1";
    // No source attached — fall back to whatever the enum hints at,
    // else default to V3 so empty-state panels explain the gap.
    if (sourceType === "2d") return "V1";
    if (sourceType === "3d") return "V2";
    return "V3";
  }, [has2D, has3D, sourceType]);

  const activityEvents = useMemo(() => {
    return (changes || []).map((c) => ({
      _id: c._id,
      who: c.userId || c.guestId || { name: "Unknown" },
      timestamp: c.timestamp,
      changeType: c.changeType,
      description: c.description,
      text: c.description,
      // A comment is a comment because a person typed it, not because
      // nothing else matched - the thread reads the changeType directly
      // now, and this only marks the two milestone rows.
      kind: c.changeType === "resolved" ? "resolved"
        : (c.changeType === "add" && c.objectType === undefined) ? "opened"
        : c.changeType === "comment" ? "comment"
        : "system",
    }));
  }, [changes]);

  // The share endpoint answers "owner" for the issue's creator and "edit"
  // for collaborators / stakeholders / owner-company members - both work
  // the issue. (Only "edit" was honoured before, so creators - e.g. the
  // account that imported the issues - got a read-only page.)
  const canEdit = permission === "edit" || permission === "owner";

  // Persist IssueTool edits back to the server. IssueTool hands over
  // { objects, canvasState, issueImage, … } — map the vector state onto
  // the schema fields PUT /issue/:id accepts, preserving 3d-pin objects
  // that share the issueObjects array with the 2D markup.
  const vectorPayload = useCallback((payload) => {
    if (!Array.isArray(payload?.objects)) return payload;
    const pins = (issue?.issueObjects || []).filter((o) => o?.objectType === "3d-pin");
    return {
      issueObjects: [...pins, ...payload.objects],
      ...(payload?.canvasState ? { canvasState: payload.canvasState } : {}),
    };
  }, [issue?.issueObjects]);

  // Paste a screenshot anywhere on the page (Ctrl/Cmd+V) -> it becomes the
  // issue's new default image (a new version); the previous image stays in
  // the version history below the meta strip.
  const [pasting, setPasting] = useState(false);
  useEffect(() => {
    if (!issue?._id || !canEdit) return undefined;
    const onPaste = async (e) => {
      const tag = document.activeElement?.tagName;
      if (["INPUT", "TEXTAREA"].includes(tag)) return; // typing in a field
      const items = e.clipboardData?.items || [];
      const img = [...items].find((it) => it.type && it.type.startsWith("image/"));
      // Non-image files on the clipboard (PDF / Excel / video copied from
      // the file explorer) attach to the issue instead of replacing the image.
      const others = [...(e.clipboardData?.files || [])].filter((f) => !String(f.type || "").startsWith("image/") && mediaKindOf(f));
      if (!img && !others.length) return;
      e.preventDefault();
      setPasting(true);
      try {
        // Whatever was pasted lands ON the canvas, not just in the
        // attachments strip: an image becomes the issue's image, a video
        // starts playing, a PDF or spreadsheet opens in the document
        // pane. The surface follows the paste.
        if (others.length) {
          const added = await uploadPastedFiles(issue._id, others);
          const first = (added || [])[0];
          if (first && first.url) {
            const kind = String(first.kind || "").toLowerCase();
            const isVideo = kind === "video" || /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(first.url);
            setMediaUrl(first.url);
            chooseSurface(isVideo ? "video" : "document");
          }
        }
        const file = img ? img.getAsFile() : null;
        if (file) {
          const dataUrl = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
          const res = await saveIssueMarkup(issue._id, { imageData: dataUrl, note: "pasted screenshot" });
          message.success(`Screenshot added as the new image${res?.version ? ` (v${res.version})` : ""}`);
          setViewVersion(null);
          chooseSurface("image");
        }
        await loadAll();
      } catch (err) {
        message.error(err.message || "Could not attach the pasted content");
      } finally {
        setPasting(false);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [issue?._id, canEdit, loadAll]);

  // Page-level Save: every mounted markup pane registers its saver here;
  // "Save" runs them, posts a pending comment draft, then refreshes.
  const sheetSaveRef = useRef(null);
  const shotSaveRef = useRef(null);
  const [savingAll, setSavingAll] = useState(false);
  const handleSaveAll = useCallback(async () => {
    setSavingAll(true);
    try {
      // onlyIfDirty: both panes are mounted on a hybrid issue and both
      // register a saver, so saving unconditionally filed a fresh copy
      // of each untouched picture on every press. A pane that was not
      // drawn on answers "unchanged" and writes nothing.
      const savers = [sheetSaveRef.current, shotSaveRef.current].filter(Boolean);
      let wrote = 0;
      for (const fn of savers) { if ((await fn({ onlyIfDirty: true })) !== "unchanged") wrote += 1; }
      const note = commentDraft.trim();
      if (note && issue?._id) {
        await addChange(issue._id, { changeType: "comment", description: note });
        setCommentDraft("");
      }
      if (!wrote && !note) message.info("Nothing new to save");
      else message.success("All changes saved");
      await loadAll();
    } catch (e) {
      message.error(e.message || "Save failed");
    } finally {
      setSavingAll(false);
    }
  }, [commentDraft, issue?._id, loadAll]);

  // Save handler for BOTH markup surfaces (2D sheet / document and the
  // raster screenshot). IssueTool composes the annotated image into
  // `issueInfo.issueImage`; persisting only the vector state - which the
  // sheet path used to do - left `sourceModelViewer.screenshot` pointing
  // at the PRE-markup picture, so every notification that embeds the
  // issue image (status change, assignment, reminder) showed the
  // assignee a document without the markup that was just drawn on it.
  // Sending the raster stores it as a new version and makes it the
  // current image. A tainted canvas yields no raster; the vector state
  // still saves.
  const handleSaveIssue = useCallback(async (payload) => {
    if (!issue?._id || !canEdit) return;
    try {
      const vec = vectorPayload(payload);
      const res = await saveIssueMarkup(issue._id, {
        imageData: payload?.issueImage || null,
        issueObjects: vec.issueObjects,
        canvasState: vec.canvasState,
      });
      if (res?.unchanged) message.info("No change on the canvas — the image was left as it is");
      else if (res?.version) message.success(`Markup saved — image v${res.version}`);
      else message.success("Markup saved");
      // The save is the newest entry of the media history and the
      // issue's picture from now on: show it, whatever was open before.
      if (!res?.unchanged) setViewVersion(null);
      await loadAll();
    } catch (e) {
      message.error(e.message || "Failed to save markup");
    }
  }, [issue?._id, canEdit, loadAll, vectorPayload]);

  // The screenshot surface saves exactly the same way.
  const handleMarkupSave = handleSaveIssue;

  // Screenshot can live in several places depending on how the issue was
  // raised/updated: viewer snapshot, a resolution element's screenshotUrl,
  // the legacy thumbnail, or resolution.screenshot.
  const bestScreenshot =
    issue?.sourceModelViewer?.screenshot ||
    (issue?.resolvedElements || []).find((e) => e?.screenshotUrl)?.screenshotUrl ||
    issue?.thumbnail || issue?.resolution?.screenshot || "";
  const hasScreenshot = !!bestScreenshot;

  // Which canvas this issue is showing. Seeded from what the issue was
  // raised on and from what a plugin has since pushed against it, then
  // remembered per issue - see helpers/issueSurface.
  const [surface, setSurface] = useState("");
  const surfaceIssueRef = useRef("");
  // Which video / document the canvas is showing (the surface says which
  // KIND, this says which one).
  const [mediaUrl, setMediaUrl] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  // Callbacks defined above the render-time `activeSurface` need to know
  // which canvas is showing; a ref keeps them out of the dependency
  // churn a state read would cause.
  const activeSurfaceRef = useRef("image");
  const [capturing, setCapturing] = useState(false);
  // The primary pane, so a capture can find whatever is on it.
  const primaryPaneRef = useRef(null);

  // The pushed model change behind this resolution, and whether the
  // reviewer is currently looking at it in 3D.
  const [resDiff, setResDiff] = useState(null);
  const [resDiffLoading, setResDiffLoading] = useState(false);
  const [reviewing3D, setReviewing3D] = useState(false);
  const [editsOpen, setEditsOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState("");

  // The proof-of-fix, which is a different picture from the one above:
  // `bestScreenshot` is where the issue was RAISED, these are what the
  // fixer captured after fixing it. While the resolution waits for review
  // they exist only on the proposal, which is why the rail used to read
  // "REQUIRED" next to a Revit push that had attached one all along.
  const resolutionShots = resolutionScreenshots(issue);
  const resolutionShot = resolutionShots[0] || "";
  const resolutionInfo = issue?.pendingResolution || null;

  // The commit the plugin pushed with the resolution describes what
  // changed in the model. Nothing on the issue read it before, so a
  // reviewer had the fixer's word and a screenshot and no way to see
  // which 49 elements moved.
  const resCommitId = issue?.pendingResolution?.commitId || issue?.resolution?.commitId || null;
  useEffect(() => {
    let alive = true;
    if (!resCommitId || isGuest) { setResDiff(null); return undefined; }
    setResDiffLoading(true);
    getCommitDiff(resCommitId, { limit: 2000 })
      // api/index.js's get() resolves error bodies too - a payload with
      // no summary is a failure, not an empty diff.
      .then((r) => {
        const body = r && r.data ? r.data : r;
        if (alive) setResDiff(body && body.summary ? body : null);
      })
      .catch(() => { if (alive) setResDiff(null); })
      .finally(() => { if (alive) setResDiffLoading(false); });
    return () => { alive = false; };
  }, [resCommitId]);

  // Approve / send back, from inside the 3D review itself - the reviewer
  // is looking at the change, which is the moment the decision gets made.
  const decideProposal = useCallback(async (action) => {
    const id = issue?.pendingResolution?.proposalId;
    if (!id) return;
    setReviewBusy(action);
    try {
      const res = await fetch(`${url}/issue/proposals/${id}/${action}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem(tokenRef)}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ note: commentDraft || "" }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.err || `HTTP ${res.status}`);
      message.success(action === "approve" ? "Resolution approved" : "Sent back to the contributor");
      setReviewing3D(false);
      await loadAll();
    } catch (e) {
      message.error(e.message || `Could not ${action} the resolution`);
    } finally {
      setReviewBusy("");
    }
  }, [issue?.pendingResolution?.proposalId, loadAll, commentDraft]);

  useEffect(() => {
    const id = issue?._id ? String(issue._id) : "";
    if (!id || surfaceIssueRef.current === id) return;
    surfaceIssueRef.current = id;
    const remembered = recallSurface(id);
    const options = availableSurfaces(issue);
    setSurface(remembered && options.includes(remembered) ? remembered : defaultSurfaceFor(issue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?._id]);

  const chooseSurface = useCallback((next) => {
    setSurface(next);
    rememberSurface(issue?._id, next);
  }, [issue?._id]);

  // Put what the canvas is showing onto a new image version, then show it.
  //
  // Three surfaces, three ways to read a pixel buffer: a <video> frame is
  // drawn to a scratch canvas, a PDF page already IS a 2D canvas, and the
  // 3D view is a WebGL canvas whose buffer is thrown away after each
  // frame unless we ask for it back — hence the preserveDrawingBuffer
  // dance and the forced re-render before reading it.
  // Read whatever the canvas is showing into a PNG data URL. Three
  // surfaces, three ways to get at a pixel buffer: a <video> frame is
  // drawn to a scratch canvas, a PDF page already IS a 2D canvas, and
  // the 3D view is a WebGL canvas whose buffer is discarded after each
  // frame unless asked for - hence preserveDrawingBuffer and the forced
  // re-render before reading it.
  const captureCanvasDataUrl = useCallback(async () => {
    const root = primaryPaneRef.current;
    const viewer = activeIssueViewer.current && activeIssueViewer.current.viewer;

    if (activeSurfaceRef.current === "3d") {
      // Draw and read in the same task: the renderer keeps no preserved
      // buffer, so a bare read of the canvas would be blank. No fallback
      // to the DOM canvas for the 3D surface for the same reason.
      try {
        if (viewer && typeof viewer.captureFrame === "function") return viewer.captureFrame("image/png") || "";
        if (viewer && viewer.renderer && viewer.scene && viewer.camera) {
          viewer.renderer.render(viewer.scene, viewer.camera);
          return viewer.renderer.domElement.toDataURL("image/png");
        }
      } catch (e) { /* nothing to capture */ }
      return "";
    }
    if (!root) return "";
    const video = root.querySelector("video");
    const canvas = root.querySelector("canvas");
    const img = root.querySelector("img");
    if (video && video.videoWidth) {
      const c = document.createElement("canvas");
      c.width = video.videoWidth; c.height = video.videoHeight;
      c.getContext("2d").drawImage(video, 0, 0, c.width, c.height);
      return c.toDataURL("image/png");
    }
    if (canvas && canvas.width > 1) return canvas.toDataURL("image/png");
    if (img && img.src) return img.src;
    return "";
  }, []);

  const captureToMarkup = useCallback(async () => {
    if (!issue?._id) return;
    setCapturing(true);
    try {
      const dataUrl = await captureCanvasDataUrl();

      if (!dataUrl) {
        message.warning("There is nothing on the canvas to capture yet.");
        return;
      }
      const res = await saveIssueMarkup(issue._id, { imageData: dataUrl, note: `captured from the ${activeSurfaceRef.current} canvas` });
      // A 3D capture is only half of a markup: the drawing is true from
      // ONE camera, and without it the annotation floats over whatever
      // angle the next reader happens to leave the model at. Pin the
      // view the shot was taken from to the version it produced, so the
      // pane can lay the markup back over the live model - and drop it
      // the moment anyone orbits away. Failing to pin must not lose the
      // capture, so it is deliberately not awaited into the error path.
      if (activeSurfaceRef.current === "3d" && typeof activeIssueViewer.pinView === "function") {
        try {
          await activeIssueViewer.pinView({ screenshotUrl: res?.url || "", screenshotVersion: res?.version || 0 });
        } catch (e) { /* the picture is saved either way */ }
      }
      message.success(`Captured${res?.version ? ` as image v${res.version}` : ""} — draw on it and save`);
      setViewVersion(null);
      chooseSurface("image");
      await loadAll();
    } catch (e) {
      // A tainted canvas (a cross-origin image drawn into it) is the one
      // failure worth naming - the rest are already loud enough.
      message.error(/tainted|SecurityError/i.test(String(e?.message))
        ? "The browser blocked reading this canvas (cross-origin content)."
        : (e?.message || "Nothing could be captured"));
    } finally {
      setCapturing(false);
    }
  }, [issue?._id, loadAll, chooseSurface, captureCanvasDataUrl]);

  // Submit: everything the assignee did, handed back in one movement.
  //
  // Save first (markup panes + any pending comment), then put the canvas
  // as it now stands on the issue - that image is what the notification
  // mails embed and what the thumbnail is generated from, so a reviewer
  // opening their inbox sees the state the work was left in rather than
  // the picture the issue was raised with. Then announce it.
  // The header bar pins to the top of the window while the page scrolls -
  // status / priority / assignee and Submit stay reachable from anywhere
  // in a long issue instead of only from the top of it.
  const [stickySentinel, headerStuck] = useStuck();

  const [submitting, setSubmitting] = useState(false);
  const submitToReviewer = useCallback(async () => {
    if (!issue?._id) return;
    setSubmitting(true);
    try {
      // 1. persist markup / comment exactly as the Save button does -
      //    and, like it, only for panes that were actually drawn on.
      //    Submitting used to copy every picture on the issue again,
      //    which is how a submitted issue ended up with the same
      //    screenshot four or five times over in its image history.
      const savers = [sheetSaveRef.current, shotSaveRef.current].filter(Boolean);
      let wrote = 0;
      for (const fn of savers) { if ((await fn({ onlyIfDirty: true })) !== "unchanged") wrote += 1; }
      const note = commentDraft.trim();
      if (note) setCommentDraft("");

      // 2. SUBMITTING IS NOT ITSELF A PICTURE.
      //
      // This used to grab the canvas on every submit from a 3D / video /
      // document surface, so submitting an issue twice left two identical
      // "submitted from the 3d canvas" versions in its image history, and
      // opening an issue, touching nothing and submitting attached its
      // own picture back to it as a new one.
      //
      // The canvas is worth capturing in exactly two cases: somebody drew
      // (handled above - the pane saved its own composed image), or the
      // issue has no picture at all, where there is nothing to duplicate
      // and the notification mails would otherwise embed nothing. Anyone
      // who wants a snapshot of the view for its own sake has the
      // "Capture for markup" button.
      const nothingToDuplicate = !hasScreenshot;
      if (!wrote && nothingToDuplicate && !["image", "sheet"].includes(activeSurfaceRef.current)) {
        try {
          const dataUrl = await captureCanvasDataUrl();
          if (dataUrl) {
            await saveIssueMarkup(issue._id, { imageData: dataUrl, note: `submitted from the ${activeSurfaceRef.current} canvas` });
            setViewVersion(null);
          }
        } catch (e) { /* a capture failure must not block the submission */ }
      }

      // 3. announce it
      const res = await submitIssue(issue._id, { note });
      const n = res?.notified || {};
      const people = (n.users || 0) + (n.contacts || 0) + (n.supervisors || 0);
      message.success(
        `Submitted${res?.statusChanged ? ` — now ${String(res.status).replace(/_/g, " ")}` : ""}` +
        (people ? ` · ${people} notified${n.supervisors ? ` (incl. ${n.supervisors} supervisor${n.supervisors === 1 ? "" : "s"})` : ""}` : "")
      );
      await loadAll();
    } catch (e) {
      message.error(e?.err || e?.message || "Could not submit the issue");
    } finally {
      setSubmitting(false);
    }
  }, [issue?._id, commentDraft, loadAll, captureCanvasDataUrl, hasScreenshot]);

  // Camera: a picture becomes the issue image, a recording is attached
  // and starts playing on the canvas.
  const onCameraPhoto = useCallback(async (dataUrl) => {
    const res = await saveIssueMarkup(issue._id, { imageData: dataUrl, note: "camera capture" });
    message.success(`Picture saved${res?.version ? ` as image v${res.version}` : ""}`);
    setViewVersion(null);
    chooseSurface("image");
    await loadAll();
  }, [issue?._id, loadAll, chooseSurface]);

  const onCameraVideo = useCallback(async (file) => {
    const r = await uploadIssueAttachments(issue._id, [file]);
    const added = (r?.added || [])[0];
    message.success("Recording attached");
    if (added?.url) setMediaUrl(added.url);
    chooseSurface("video");
    await loadAll();
  }, [issue?._id, loadAll, chooseSurface]);

  // Picking a model version / plugin export in the SOURCE bar is a
  // request to look at it: reload the issue and bring the 3D canvas up,
  // which is also the moment the streaming session starts. Attaching a
  // sheet or pasting media switches to that surface for the same reason.
  const onSourceChanged = useCallback(async (what) => {
    await loadAll();
    if (!what) return;
    if (what.sourceModel) chooseSurface("3d");
    else if (what.sheet) chooseSurface("sheet");
    else if (what.attachments) {
      // "Paste media" lands on the canvas exactly like Ctrl+V does.
      const first = (what.added || [])[0];
      if (!first || !first.url) return;
      const kind = String(first.kind || "").toLowerCase();
      setMediaUrl(first.url);
      if (kind === "image") { setViewVersion(null); chooseSurface("image"); }
      else if (kind === "video" || /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(first.url)) chooseSurface("video");
      else chooseSurface("document");
    }
  }, [loadAll, chooseSurface]);

  // A reviewer opening a submitted fix should be looking AT the fix. Put
  // the resolution screenshot on the canvas once per submission; the
  // image-history strip (and its "Back to current" button) moves away
  // from it, and a later paste/markup replaces the selection anyway.
  const autoShotRef = useRef("");
  useEffect(() => {
    const key = resolutionInfo?.proposalId || "";
    if (!key || resolutionInfo.status !== "pending" || !resolutionShot) return;
    if (autoShotRef.current === key) return;
    autoShotRef.current = key;
    setViewVersion({
      url: resolutionShot,
      resolution: true,
      channel: resolutionInfo.channel || "",
      name: resolutionInfo?.proposer?.name || "",
      createdAt: resolutionInfo.submittedAt || null,
    });
  }, [resolutionInfo?.proposalId, resolutionInfo?.status, resolutionShot]); // eslint-disable-line react-hooks/exhaustive-deps
  // assignedTo is an object ({userId}), responsibleUsers an array — the old
  // `(responsibleUsers || assignedTo || []).length` read .length off an object.
  // Whether the signed-in user is on the owner's side of this project.
  // Drives the resolve gate below and the Abandon control.
  const projAccess = useProjectAccess(
    issue && issue.projectId ? String(issue.projectId._id || issue.projectId) : ""
  );
  const isOwnerSide = !isGuest
    && !!(projAccess?.isOwnerCompany || projAccess?.isProjectOwner);

  // WHO GETS THE DELIVERABLE when this issue is approved (owner,
  // 2026-09-30). Owner-side only: it decides who receives a customer's
  // file, which is not a partner's call.
  //
  // ITS OWN PANEL RATHER THAN A GENERAL EDIT FORM, because this page has
  // never had one - it calls updateIssueStatus and the resolve flows and
  // nothing else. Adding a general issue editor to hang one field on it
  // would be a bigger thing than he asked for, and everything after would
  // then be added to it.
  const [recipRoster, setRecipRoster] = useState([]);
  const [recipValue, setRecipValue] = useState(null); // null = not loaded from the issue yet
  const [recipSaving, setRecipSaving] = useState(false);
  const issueProjectId = issue && issue.projectId ? String(issue.projectId._id || issue.projectId) : "";
  useEffect(() => {
    if (!isOwnerSide || !issueProjectId) return;
    let alive = true;
    getParticipantsRoster(issueProjectId)
      .then((r) => { if (alive) setRecipRoster(Array.isArray(r) ? r : []); })
      .catch(() => { if (alive) setRecipRoster([]); });
    return () => { alive = false; };
  }, [isOwnerSide, issueProjectId]);
  // The issue's own list arrives with the issue; hold it locally so typing
  // does not fight the reload.
  useEffect(() => {
    if (!issue) return;
    setRecipValue((issue.recipients || []).map((r) => String(r.email || "").toLowerCase()).filter(Boolean));
  }, [issue?._id, issue?.recipients]);

  // SAVING THE LINKED TO ROWS. One function, threaded to all three layout
  // bodies, because the block renders in every one of them and three copies
  // of a save is how two of them come to disagree.
  // WHO MAY CHANGE DISCIPLINE / CONTRACTOR / TAGS here: the page's edit
  // right AND issueEdit on the project (PUT /issue/:id is gated on it), and
  // never the contractor through a shared link (the server refuses that).
  // Until the access answer arrives the server stays the judge.
  const linkedOpts = {
    canPickContractor: !isGuest,
    canEditFields: isGuest || !projAccess?.access || !!projAccess?.can?.issueEdit,
  };
  const saveLinkedFields = useCallback(async (patch) => {
    try {
      await updateIssue(issue._id, patch);
      message.success("Saved");
      loadAll();
      // AND TELL THE LIST (owner, 2026-10-22: "when a contractor or activity
      // or any other property get changed inside the issue single view,
      // especially from the linked to column/panel, the filter on the full
      // list failes to detect the changes").
      //
      // loadAll() reloads THIS page only. refreshIssueLists() is what the
      // Issues Manager and the Approval Queue watch - both have refreshTick
      // in the dependency list of their load() - and nothing in this page
      // ever dispatched it. So the edit was saved, the single view showed
      // the new contractor, and a list still holding the old row filtered on
      // the old value. The server was right the whole time; the screen
      // asking it was out of date.
      dispatch(refreshIssueLists());
    } catch (e) {
      message.error(e.message || "Could not save");
      throw e;   // so the block keeps the draft open rather than clearing it
    }
  }, [issue && issue._id, loadAll]);

  const hasTaggedUsers =
    (issue?.responsibleUsers || []).length > 0 || !!issue?.assignedTo?.userId ||
    (issue?.responsibleContacts || []).length > 0;
  // A linked model commit is only a closure requirement when the issue
  // actually has a 3D model — screenshot / 2D-only issues (which fall
  // back to the hybrid layout) must not be blocked on it.
  const needsCommit = has3D;
  // Schema field is resolution.commitId; commitSha only ever existed on
  // sourceModel snapshots.
  const hasCommit = !!(issue?.resolution?.commitId || issue?.resolution?.commitSha || issue?.sourceModel?.commitSha);
  // Evidence policy demanded at creation. Screenshot / document can still
  // be supplied in the resolve dialog, so they do not block the button;
  // model push (no linked commit) and plugin-only resolves do.
  const evPolicy = issue?.evidencePolicy || {};
  const policyNeedsCommit = !!evPolicy.modelPush;
  const policyPluginOnly = !!evPolicy.pluginResolve;
  const hasDocument = (issue?.attachments || []).some((a) => ["pdf", "excel", "doc", "document"].includes(a?.kind) || /\.(pdf|xlsx?|xlsm|csv|docx?)(\?|$)/i.test(a?.url || a?.name || ""));
  // (the screenshot itself is supplied in the resolve dialog when missing)
  // "Needs a responsible person" is a rule about CREDIT: a proposal has
  // to say whose fix it was. It is not a rule about authority, and
  // applying it to the owner side blocked the people whose issue it is
  // from closing out their own work - a project admin or a member
  // looking at an unassigned issue found Mark resolved greyed out with
  // no way to proceed except assigning somebody first.
  //
  // So the owner side (project owner, owner company, or anyone on the
  // project roster) may resolve an unassigned issue. Everyone else still
  // needs a responsible person named, because their resolution goes to
  // the Review Center as a claim about somebody's work.
  //
  // The evidence policy set at creation is NOT waived: a model push or a
  // plugin-only resolve was a deliberate requirement, not an oversight.
  const canResolve = (hasTaggedUsers || isOwnerSide)
    && (!needsCommit || hasCommit) && (!policyNeedsCommit || hasCommit)
    && !policyPluginOnly && issue?.status !== "resolved";
  const resolveHint = isGuest
    ? "Use the Status control above to mark this resolved"
    : (policyPluginOnly && issue?.status !== "resolved")
      ? "This issue requires the resolution to be pushed from the Revit / Navisworks plugin"
      : (policyNeedsCommit && !hasCommit)
        ? "This issue requires a model push / diff from Revit or Navisworks (no linked commit yet)"
        : resolveBlockReason({ canResolve, hasScreenshot: true, hasTaggedUsers: hasTaggedUsers || isOwnerSide, needsCommit, hasCommit, status: issue?.status });

  const handlePostComment = async () => {
    if (!commentDraft.trim() || !issue?._id) return;
    setPosting(true);
    try {
      await addChange(issue._id, { changeType: "comment", description: commentDraft.trim() });
      setCommentDraft("");
      await loadAll();
    } catch (e) {
      message.error(e.message || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  // "Mark resolved" opens the evidence dialog; the dialog's submit lands
  // here with the resolution screenshot(s) / document(s) + note.
  const openResolve = () => {
    if (!canResolve) { message.warning("Resolution requirements not met"); return; }
    setResolveOpen(true);
  };
  const handleMarkResolved = async ({ files = [], notes = "", hoursSpent = null } = {}) => {
    if (!canResolve) { message.warning("Resolution requirements not met"); return; }
    setResolving(true);
    try {
      // 1. Upload the resolution evidence as attachments (role
      //    "resolution") - the server files each one in Document Control
      //    as "Resolution screenshot / document".
      let evidence = [];
      let uploadedShots = [];
      if (files.length) {
        const up = await uploadIssueAttachments(issue._id, files, { role: "resolution" });
        evidence = (up?.added || []).map((a) => a._id).filter(Boolean);
        uploadedShots = (up?.added || []).filter((a) => a.kind === "image").map((a) => a.url);
      }
      const evidenceShot = uploadedShots[0] || bestScreenshot;
      // Roadmap item #3 — route through PUT /issue/:id/resolve which
      // enforces the closure checklist server-side. The body shape is
      // documented in back/routes/issue.js. We synthesize the four
      // required fields from data already on the page:
      //
      //   commitId           — issue.resolution.commitSha or sourceModel
      //                         .commitSha. If both empty the backend
      //                         returns failures[].linkedCommit which we
      //                         surface as a toast.
      //   resolvedElements   — selectedElements from the viewer if
      //                         present; else a single placeholder
      //                         carrying the thumbnail as screenshotUrl.
      //   afterFragmentHashes — fragment hashes of the current
      //                         selectedElements (best-effort).
      //   notes              — pulled from the last comment draft if not
      //                         posted, else empty.
      const commitId = issue?.resolution?.commitId
        || issue?.resolution?.commitSha
        || issue?.sourceModel?.commitSha
        || "";
      const selected = issue?.sourceModelViewer?.selectedElements || [];
      // Bolt-on 2 — auto-detected candidates fill in when the plugin
      // push didn't carry a selectedElements list (typical for
      // web-created issues). "Modified" + "added" default in; the
      // user can still refine before submitting.
      const autoCandidates = (selected.length === 0 && candidateFix)
        ? [
            ...(candidateFix.modified || []).map((e) => ({
              uniqueId: e.uniqueId,
              fragmentHash: "", // server computes fresh hash for this uid
              tileKey: e.tileKey || "",
            })),
            ...(candidateFix.added || []).map((e) => ({
              uniqueId: e.uniqueId,
              fragmentHash: "",
              tileKey: e.tileKey || "",
            })),
          ]
        : [];
      const pickedElements = selected.length > 0 ? selected : autoCandidates;
      const resolvedElements = pickedElements.length > 0
        ? pickedElements.map((el) => ({
            uniqueId: el.uniqueId || el.id || String(el),
            fragmentHash: el.fragmentHash || "",
            tileKey: el.tileKey || "",
            screenshotUrl: evidenceShot,
          }))
        : [{
            uniqueId: "thumbnail-only",
            screenshotUrl: evidenceShot,
          }];

      const afterFragmentHashes = pickedElements
        .map((el) => el.fragmentHash)
        .filter(Boolean);

      // POST a proposal. If the project has requireProposalApproval=false
      // the backend auto-approves in the same write (response.autoApproved
      // === true) → toast "Marked resolved." Otherwise → toast "Submitted
      // for review."
      //
      // The legacy PUT /issue/:id/resolve still exists but now requires
      // the ISSUE_RESOLVE_DIRECT permission and is reserved for admins
      // who need to bypass the approval queue.
      // A partner working through their emailed link has no account, so
      // there is no proposer to attach a proposal to. Their resolution
      // goes through the status route instead - which has accepted guest
      // sessions all along, files the same evidence, and lands in the
      // owner's Review Center exactly the same way (it answers
      // pendingReview:true when it does).
      if (isGuest) {
        const r = await updateIssueStatus(issue._id, "resolved", {
          notes: String(notes || "").trim() || commentDraft.trim(),
          evidence,
          hoursSpent,
        });
        message.success(
          r && r.pendingReview
            ? (r.message || "Marked resolved — sent to the Review Center; it counts as resolved once approved")
            : "Marked resolved — everyone on the issue has been notified"
        );
        setResolveOpen(false);
        setCommentDraft("");
        await loadAll();
        return;
      }

      const resp = await fetch(`${url}/issue/${issue._id}/proposals`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // tokenRef is the env-scoped key (bidlight_token / prebidlight_token);
          // the old host-derived key (app_token / predeployment_token) stopped
          // matching on 2026-08-15 and made this call 401.
          Authorization: `Bearer ${localStorage.getItem(tokenRef)}`,
        },
        body: JSON.stringify({
          commitId,
          resolvedElements,
          afterFragmentHashes,
          notes: String(notes || "").trim() || commentDraft.trim(),
          // Channel stamping — this page is the web submission path.
          submissionChannel: "web",
          elementPickMode: selected.length > 0 ? "manual" : "auto",
          screenshotUrls: [...uploadedShots, bestScreenshot].filter(Boolean),
          // Uploaded resolution files (attachment ids) -> proposal.evidence.
          evidence,
          // Required with every resolution (back/utils/issueHours).
          hoursSpent,
        }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        message.error(body.err || "Failed to submit proposal");
        return;
      }
      message.success(
        body.autoApproved
          ? "Marked resolved - evidence filed in Document Control"
          : "Submitted for review — reviewer will be notified"
      );
      // An auto-approved proposal resolves the issue there and then, so the
      // lists behind this page (issue manager, Review Center, dashboards)
      // drop it now instead of at their next reload (reducers/issues.js).
      if (body.autoApproved) store.dispatch(issueFinished(String(issue._id), "resolved"));
      setResolveOpen(false);
      await loadAll();
    } catch (e) {
      message.error(e.message || "Failed to mark resolved");
    } finally {
      setResolving(false);
    }
  };

  if (loading) return <div style={{ padding: 40 }}><Skeleton active paragraph={{ rows: 10 }} /></div>;
  if (err) return <div style={{ padding: 40, color: T.removed2 }}>{err}</div>;
  if (!issue) return <div style={{ padding: 40 }}>Issue not found.</div>;
  if (needsGuestInfo) {
    // A PUBLIC LINK OPENS FOR THE PUBLIC (owner, 2026-10-28: "when opened
    // with public access, i get rerouted to login ... it should allow me
    // public access"). The share route only answers an anonymous visitor
    // when public access is on (403 otherwise), so reaching here means the
    // issue IS public: ask for name and email, as the share settings say.
    // Until 2026-10-28 a link without ?g= went to /authorization instead,
    // which sent every public share link to the login page. Someone with
    // an account still gets there - the form's "Sign in instead".
    const signInState = { from: `${window.location.pathname}${window.location.search || ""}` };
    return (
      <div style={{ background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
        <GuestRegistrationModal
          visible
          required
          shareToken={shareToken}
          issueTitle={issue.title}
          onSuccess={(g) => { setGuest(g); setNeedsGuestInfo(false); loadAll(); }}
          onCancel={() => {}}
          onSignIn={() => history.push({ pathname: "/authorization", state: signInState })}
        />
      </div>
    );
  }

  // Real minted display id ("SPX-126"); issueLabel falls back to the
  // Mongo _id's last hex chars when the counter never minted one.
  const issueId = issueLabel(issue);
  const rawSheet = issue.sourceDocument?.sheetNumber || issue.sourceDocument?.name || "";
  // discipline is a plain string on the issue schema; older docs carried {key}.
  const discLetter = (typeof issue.discipline === "string" ? issue.discipline : issue.discipline?.key)
    || issue.sourceDocument?.discipline?.key || "";
  const raisedAt = issue.createdAt ? moment(issue.createdAt).format("MMM D, YYYY") : "";
  const milestoneLabel = issue.milestone?.value || issue.milestone?.key || issue.milestone || "";
  const assignees = [
    ...(Array.isArray(issue.responsibleUsers) ? issue.responsibleUsers : []),
    ...(issue.assignedTo?.userId ? [issue.assignedTo] : []),
    // No-account responsibles (magic-link contacts) — shown like users.
    ...((issue.responsibleContacts || []).map((c) => ({ _id: `contact:${c.email}`, name: c.name || c.email, email: c.email, contact: true }))),
  ];
  const assigneeNames = assigneeSummary(assignees);
  const markupInfo = markupSummary(issue);
  // Image versions, newest first (every paste / markup save, the original
  // last). Selecting an older one shows it read-only.
  const imageVersions = (issue.screenshotVersions || []).slice().sort((a, b) => (b.version || 0) - (a.version || 0));
  const currentVersion = imageVersions[0] || null;
  // The resolution's screenshots ride in the same strip, first, so the
  // picture a reviewer needs is one click from the canvas instead of
  // living only in a 200px rail thumbnail.
  const resolutionSlides = resolutionShots.map((u) => ({
    url: u,
    resolution: true,
    channel: resolutionInfo?.channel || "",
    name: resolutionInfo?.proposer?.name || "",
    createdAt: resolutionInfo?.submittedAt || issue?.resolution?.resolvedAt || null,
  }));
  const versionSlides = [...resolutionSlides, ...imageVersions];
  // `surface` is empty for the first render after a load; fall back to
  // the rule rather than flashing the wrong canvas.
  const activeSurface = surface || defaultSurfaceFor(issue);
  activeSurfaceRef.current = activeSurface;
  // ── MEDIA HISTORY: every source medium the issue has collected ──
  // The picture it was raised with, each paste / markup save / attached
  // image (versions), every other attached or recorded file, and each
  // sheet picked or imported - newest first, so the latest thing that
  // happened to the issue is on the left. Resolution evidence is not
  // here: that is the DELIVERED row above it.
  const byNewest = (a, b) => (new Date(b.createdAt || 0) - new Date(a.createdAt || 0)) || ((b.order || 0) - (a.order || 0));
  const versionUrls = new Set(imageVersions.map((v) => v.url));
  const mediaSlides = [];
  imageVersions.forEach((v, i) => mediaSlides.push({
    key: `v-${v.version}-${v.url}`, kind: "image", url: v.url, version: v.version,
    label: v.version ? `v${v.version}` : "Image", name: v.note === "original" ? "original image" : (v.note || ""),
    who: v.name, createdAt: v.createdAt || issue.createdAt, order: 1000 - i,
    isCurrent: !!(currentVersion && v.url === currentVersion.url),
  }));
  if (bestScreenshot && !versionUrls.has(bestScreenshot)) {
    mediaSlides.push({ key: `raised-${bestScreenshot}`, kind: "image", url: bestScreenshot, label: "Raised with", name: "issue image", createdAt: issue.createdAt, order: 0, isCurrent: !currentVersion });
  }
  for (const a of issue.attachments || []) {
    if (!a || !a.url || String(a.role || "") === "resolution") continue;
    const kind = storedKindOf(a);
    if (kind === "image" && versionUrls.has(a.url)) continue; // promoted to a version already
    mediaSlides.push({ key: `att-${a._id || a.url}`, kind, url: a.url, label: (KIND_META[kind] || KIND_META.file).label, name: a.name, who: a.uploaderName, createdAt: a.createdAt, att: a });
  }
  const sheetRoster = (issue.sourceDocuments || []).filter((d) => d && d.url);
  if (!sheetRoster.length && issue.sourceDocument?.url) sheetRoster.push(issue.sourceDocument);
  sheetRoster.forEach((d, i) => mediaSlides.push({
    key: `sheet-${d.docControlId || d.url}`, kind: "sheet", url: d.url, sheet: d,
    label: d.sheetNumber || "Sheet", name: d.name || "", who: d.pickedBy,
    createdAt: d.pickedAt || issue.createdAt, order: -1 - i,
    isCurrent: activeSurface === "sheet" && issue.sourceDocument?.url === d.url,
  }));
  // Owner / members may remove an entry (same trash icon as the
  // attachments row). Guests keep only their own attachments route.
  const canDeleteMedia = !!canEdit && !!currentUserId;
  if (canDeleteMedia) for (const m of mediaSlides) m.deletable = true;
  mediaSlides.sort(byNewest);
  const deleteMedia = async (v) => {
    try {
      if (v.att && v.att._id) await deleteIssueAttachment(issue._id, v.att._id);
      else if (v.kind === "sheet") await deleteIssueSheet(issue._id, v.url);
      else if (v.kind === "image") await deleteIssueImage(issue._id, v.url);
      else return;
      if (viewVersion && viewVersion.url === v.url) setViewVersion(null);
      if (mediaUrl === v.url) setMediaUrl("");
      message.success("Removed from the media history");
      await loadAll();
    } catch (e) {
      message.error(e.message || "Could not remove it");
    }
  };
  // ── DELIVERED: what answered the issue ──
  // Sheets from a delivered set, a DWG / Excel / photo / video handed
  // over, and the screenshots of a submitted fix (still pending or
  // approved). One click opens each on the canvas.
  const deliveredSlides = [];
  const deliveredSeen = new Set();
  const pushDelivered = (x) => { if (!x.url || deliveredSeen.has(x.url)) return; deliveredSeen.add(x.url); deliveredSlides.push({ tone: "added", ...x }); };
  for (const a of issue.attachments || []) {
    if (!a || !a.url || String(a.role || "") !== "resolution") continue;
    const kind = storedKindOf(a);
    pushDelivered({ key: `del-${a._id || a.url}`, kind, url: a.url, label: (KIND_META[kind] || KIND_META.file).label, name: a.name, who: a.uploaderName, createdAt: a.createdAt, att: a,
      versionDocId: a.registerDocId || null });
  }
  for (const e of issue.resolution?.evidence || []) {
    if (!e || !e.url) continue;
    const kind = storedKindOf(e);
    pushDelivered({ key: `ev-${e.docControlId || e.url}`, kind, url: e.url, docControlId: e.docControlId,
      // A sheet from a delivered set is a register document (its versions); other evidence is an issue-media mirror.
      versionDocId: kind === "pdf" && e.docControlId ? e.docControlId : null,
      label: kind === "pdf" && e.docControlId ? "Sheet" : (KIND_META[kind] || KIND_META.file).label, name: e.name, createdAt: issue.resolution?.resolvedAt || issue.updatedAt });
  }
  for (const s of resolutionSlides) {
    pushDelivered({ key: `res-${s.url}`, kind: "image", url: s.url, label: `Resolution${s.channel ? ` · ${CHANNEL_LABEL[s.channel] || s.channel}` : ""}`, name: s.name, createdAt: s.createdAt, resolution: true });
  }
  deliveredSlides.sort(byNewest);
  const videos = issueVideos(issue);
  const documents = issueDocuments(issue);
  // The document / video panes list what the strips can open, including
  // delivered evidence that is filed in Doc Control rather than attached.
  const docUrls = new Set(documents.map((d) => d.url));
  const paneDocs = [...documents, ...deliveredSlides.filter((d) => ["pdf", "excel", "doc", "drawing"].includes(d.kind) && !docUrls.has(d.url)).map((d) => ({ url: d.url, name: d.name || d.label, kind: d.kind }))];
  const videoUrls = new Set(videos.map((v) => v.url));
  const paneVideos = [...videos, ...deliveredSlides.filter((d) => d.kind === "video" && !videoUrls.has(d.url)).map((d) => ({ url: d.url, name: d.name || d.label, kind: "video" }))];
  // What the strips open on the canvas: the page's surface follows the
  // kind of the thing clicked.
  const openMedia = async (v) => {
    if (!v) return;
    if (v.kind === "image") {
      setViewVersion(v.isCurrent ? null : { url: v.url, version: v.version, name: v.who, resolution: !!v.resolution });
      chooseSurface("image");
      return;
    }
    if (v.kind === "video") { setMediaUrl(v.url); chooseSurface("video"); return; }
    if (v.kind === "sheet") {
      const active = issue.sourceDocument?.url === v.url;
      if (!active && v.sheet?.docControlId && canEdit) {
        try { await setIssueSheet(issue._id, String(v.sheet.docControlId)); await loadAll(); }
        catch (e) { message.error(e.message || "Could not open that sheet"); return; }
      } else if (!active) {
        setMediaUrl(v.url); chooseSurface("document"); return;
      }
      chooseSurface("sheet");
      return;
    }
    setMediaUrl(v.url);
    chooseSurface("document");
  };
  const activeMediaUrl = activeSurface === "image" ? (viewVersion ? viewVersion.url : (currentVersion ? currentVersion.url : bestScreenshot))
    : activeSurface === "sheet" ? (issue.sourceDocument?.url || "")
      : ["video", "document"].includes(activeSurface) ? mediaUrl : "";
  // The action bar every surface gets. `canSave` is false where there is
  // no markup layer to save yet - capture first, then draw.
  const canvasActions = (
    <CanvasActions
      fullscreen={fullscreenCtl}
      canEdit={canEdit}
      capturing={capturing}
      saving={savingAll}
      canSave={activeSurface === "image" || activeSurface === "sheet"}
      canCapture={!["image", "sheet"].includes(activeSurface)}
      onCapture={captureToMarkup}
      onSave={handleSaveAll}
      onCamera={() => setCameraOpen(true)}
      captureLabel={activeSurface === "3d" ? "Screenshot for markup" : "Capture for markup"}
    />
  );
  const modelAttached = issueHasModel(issue);

  // What the 3D panes need to paint (and decide on) the pushed change.
  const review3D = resDiff
    ? {
        active: reviewing3D,
        commitId: resCommitId,
        elements: resDiff.elements || [],
        summary: resDiff.summary || {},
        changedTilesByCategory: resDiff.changedTilesByCategory || {},
        // The push that carried the fix - what the pane must stream while
        // the change is under review (the issue itself stays pinned to
        // the version it was raised against).
        manifestPath: (resDiff.meta && resDiff.meta.stream && resDiff.meta.stream.manifestPath) || "",
        busy: reviewBusy,
        ...(canReviewProposals && issue?.pendingResolution?.status === "pending"
          ? {
              onApprove: () => decideProposal("approve"),
              onReject: () => decideProposal("reject"),
            }
          : {}),
      }
    : null;
  // Clicking the rail thumbnail puts that image on the big canvas.
  const showShotOnCanvas = (u) => {
    const slide = versionSlides.find((v) => v.url === u);
    setViewVersion(slide || { url: u, resolution: true });
    // On a 2D issue the sheet was the canvas; selecting a picture has to
    // switch the canvas to it, not just remember the choice.
    chooseSurface("image");
  };
  const shownScreenshot = viewVersion ? viewVersion.url : bestScreenshot;
  // Markup edits the issue's own image; a resolution shot is somebody
  // else's evidence and an older version is history, so both are shown
  // read-only.
  // An older version or a delivered picture opened from the strips is
  // editable too: the save writes a NEW version (the markup route never
  // overwrites), which becomes the issue's picture - the thumbnail in
  // the lists and the image in the next mail - until the next save.
  const editableNow = canEdit;

  // Action toolbar above the primary pane — logged-in editors only
  // (guests and read-only share-link viewers never see it).
  // Markup/comment attribution: the logged-in user, else the guest.
  const effectiveUserInfo = currentUserId
    ? userInfoRedux
    : (guest ? { _id: guest._id, name: guest.name, email: guest.email, isGuest: true } : null);
  // Sheet select · Import sheet/image · Paste media (ASSIGNED TO lives in
  // the top bar now). Guests get their status bar plus the same source bar.
  const actionBar = canEdit
    ? (
      <>
        {isGuest && <GuestActionBar issue={issue} guest={guest} onChanged={loadAll} />}
        <IssueSourceBar issue={issue} onChanged={onSourceChanged} />
      </>
    )
    : null;
  // V1/V2/V3 render their own panes; give them the same canvas actions by
  // appending the bar to what they already put above the pane.
  const actionBarWithCanvas = (
    <>
      {actionBar}
      {canvasActions}
    </>
  );

  return (
    <div style={{ background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
      {isGuest && guest && <GuestProfileNudge guest={guest} onSaved={(g) => setGuest((prev) => ({ ...(prev || {}), ...(g || {}) }))} />}
      <IssueCameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onPhoto={onCameraPhoto}
        onVideo={onCameraVideo}
      />
      <ResolveEvidenceModal
        open={resolveOpen}
        busy={resolving}
        onCancel={() => { if (!resolving) setResolveOpen(false); }}
        onSubmit={handleMarkResolved}
        currentScreenshot={bestScreenshot}
        initialNotes={commentDraft}
        gated={!!(issue?.projectId?.requireProposalApproval || issue?.project?.requireProposalApproval)}
        required={issue?.evidencePolicy || null}
        have={{ screenshot: hasScreenshot, document: hasDocument, modelPush: hasCommit }}
      />
      {showShare && (
        <ShareIssueModal visible={showShare} issue={issue} onClose={() => setShowShare(false)}
          onUpdate={(patch) => {
            // Regenerating the link invalidates the token in OUR OWN url:
            // reloading here would 404 ("Issue not found"). Move to the new
            // link instead - the route param change re-runs loadAll.
            if (patch?.shareToken && patch.shareToken !== shareToken) {
              history.replace({ pathname: `/issue/${patch.shareToken}/resolve`, search: history.location.search });
              return;
            }
            loadAll();
          }} />
      )}
      {/* Breadcrumbs — logged-in users only; guest share-link viewers
          would just hit auth walls on these links. */}
      {currentUserId && (
        <div style={{ padding: "10px 24px 0", background: "#fff" }}>
          <CoordinationBreadcrumbs
            items={[
              { label: "Issues Manager", to: "/issues" },
              {
                label: issue.displayId
                  ? `#${issue.displayId}`
                  : issue.title || "Issue",
                to: `/issue/${shareToken}/resolve`,
              },
            ]}
            current="Resolve"
          />
        </div>
      )}
      {/* Sentinel: the last pixel before the bar pins. */}
      <div ref={stickySentinel} aria-hidden style={{ height: 1, marginBottom: -1 }} />
      {/* Top bar - PINNED just below the app's top block (header + tab
          strip, AppTopBars), which stays visible on every page (owner,
          2026-10-13) - so this bar no longer slides over the header. antd
          dropdowns and modals portal to the body at 1000+ and are
          unaffected. */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        // Tightens a little once pinned - it is a toolbar then, not a header.
        padding: headerStuck ? "8px 24px" : "12px 24px",
        background: "#fff", borderBottom: `1px solid ${T.line}`,
        position: "sticky", top: "var(--app-bars-h, 0px)", zIndex: 60,
        boxShadow: headerStuck ? "0 2px 8px rgba(35,52,62,.10)" : "none",
        transition: "box-shadow .15s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
          {currentUserId ? (
            <>
              <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => history.push("/issues")} title="Back to all issues" />
              <Link to="/issues" style={{ color: T.muted, textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                    title="Back to all issues"
                    onMouseEnter={(e) => { e.currentTarget.style.color = T.navy; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = T.muted; }}>
                Issues Manager
              </Link>
              <span style={{ color: T.sub }}>/</span>
            </>
          ) : (
            <span style={{ color: T.muted }}>BidLight issue</span>
          )}
          <span style={{ color: T.ink, fontWeight: 600 }}>{issueId}</span>
        </div>
        {/* Left: update controls (status, priority, add partner). Right:
            Refresh · Open viewer · Save (everything pending). */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: 1, marginLeft: 16 }}>
          {issue?.mergedInto && <IssueMergedBanner issue={issue} />}
          {lockedClosed && (
            <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", color: "#237804", borderRadius: 6, padding: "6px 10px", fontSize: 12.5, margin: "0 0 8px" }}>
              This issue is finished - it is read-only now. Contact the project owner if it needs to be reopened.
            </div>
          )}
          <EditorTopControls show={canEdit} guest={isGuest} issue={issue} onChanged={loadAll} me={userInfoRedux} canMerge={isOwnerSide} />
          {/* A read-only viewer has no controls up here, so the pinned bar
              would carry nothing but the issue number. Give them the same
              two facts, read-only, plus who it is on. */}
          {!canEdit && (
            <>
              <StatusPill status={issue.status} />
              <PriPill priority={issue.priority} />
              {assigneeNames && (
                <span style={{ fontSize: 11.5, color: T.muted }}>
                  Assigned to <b style={{ color: T.text }}>{assigneeNames}</b>
                </span>
              )}
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
          {canEdit && (
            <Tooltip title="Save everything, put the canvas as it stands on the issue, and send it back to the reviewer - everyone on the issue is emailed, including the partner's supervisors">
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                loading={submitting}
                onClick={submitToReviewer}
                style={{ background: T.navy, borderColor: T.navy }}
              >
                Submit
              </Button>
            </Tooltip>
          )}
          {currentUserId && canShare && (
            <Button size="small" icon={<ShareAltOutlined />} onClick={() => setShowShare(true)}
                    title="Email a link, copy the link, manage collaborators and public access">
              Share
            </Button>
          )}
          {canEdit && (
            <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSaveAll} loading={savingAll}
              title="Save everything: canvas markup (new image version), pending comment and other edits">
              Save
            </Button>
          )}
        </div>
      </div>

      {/* Issue meta strip */}
      <div style={{ padding: "16px 24px", background: "#fff", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{
            background: T.navy, color: "#fff",
            padding: "3px 8px", borderRadius: 3,
            fontSize: 11, fontWeight: 700, letterSpacing: ".02em",
          }}>{issueId}</span>
          {discLetter && (
            <span style={{
              width: 22, height: 22, borderRadius: 3,
              background: "#E9F1F5", color: T.navyD, fontWeight: 700, fontSize: 11,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>{discLetter}</span>
          )}
          <SourceTag type={sourceType} />
          <StatusPill status={issue.status} />
          <PriPill priority={issue.priority} />
          {/* APPROVED TO RESOLVE, on the top header (owner 2026-10-26: the toggle
              "on any issue in edit mode on top header"). Green when somebody has
              cleared this issue to be worked on, red when nobody has.

              ADVISORY - it blocks nothing. And only the REVIEWER SIDE may change
              it: anybody else sees the circle and the words, with no switch, so
              the state is always legible even to the person it is addressed to.
              The server checks again (utils/permissions.isOwnerSideReviewer). */}
          <ApprovedToResolvePill issue={issue} canApprove={canReviewProposals} onChanged={loadAll} />
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: T.muted }}>Raised {raisedAt}{issue?.updatedAt ? <span title={new Date(issue.updatedAt).toLocaleString()}> · Last modified {new Date(issue.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} {new Date(issue.updatedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}{issue.lastEditedBy?.userId?.name || issue.lastEditedByUser?.name ? ` by ${issue.lastEditedBy?.userId?.name || issue.lastEditedByUser?.name}` : ""}</span> : null}</span>
        </div>

        <IssueTitle title={issue.title} canEdit={editableNow && linkedOpts.canEditFields !== false}
                    onSave={(title) => saveLinkedFields({ title })} />

        <div style={{
          display: "flex", alignItems: "center", gap: 16, marginTop: 8,
          fontSize: 11.5, color: T.muted, flexWrap: "wrap",
        }}>
          {rawSheet && <span>Sheet <b style={{ color: T.text }}>{rawSheet}</b> · {issue.sourceDocument?.name}</span>}
          {(issue.sourceDocument?.revisionNumber || issue.createdRev) && (
            <span>Raised at <b style={{ color: T.text }}>R{issue.sourceDocument?.revisionNumber || issue.createdRev}</b></span>
          )}
          {milestoneLabel && <span>Milestone <b style={{ color: T.text }}>{milestoneLabel}</b></span>}
          {markupInfo && (
            <span title={markupInfo.title}>
              Markup <b style={{ color: T.text }}>v{markupInfo.version}</b> by <b style={{ color: T.text }}>{markupInfo.name}</b> · {markupInfo.when}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span>Assigned:</span>
          {assignees.slice(0, 3).map((u, i) => (
            <div key={u._id || i} style={{ marginLeft: i === 0 ? 0 : -8 }}>
              <Avatar user={u.userId && typeof u.userId === "object" ? u.userId : u} size={22} />
            </div>
          ))}
          {assignees.length > 0 && (
            <span style={{ fontSize: 11.5, color: T.text, marginLeft: 6 }}>
              {assigneeNames}
            </span>
          )}
          {assignees.length === 0 && (
            <span style={{ fontSize: 11, color: T.sub }}>unassigned</span>
          )}
        </div>
        {/* DELIVERABLE RECIPIENTS - owner-side only, its own save.
            They are mailed ONCE, when this issue is approved, with the
            deliverable attached or a download link if it is too large.
            They hear about nothing else; that is the responsible list
            above. Empty is the ordinary case and sends nobody anything. */}
        {isOwnerSide && recipValue !== null && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderTop: `1px solid ${T.line || "#eee"}`, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11.5, color: T.sub, whiteSpace: "nowrap" }}>
              Deliverable recipients
            </span>
            {/* The shared picker (components/PeoplePicker, owner
                2026-10-15): every partner under its trade, people tagged by
                kind. A recipient is a reader, so executives are offered. */}
            <PeoplePicker
              mode="tags"
              size="small"
              style={{ minWidth: 280, flex: 1 }}
              value={recipValue}
              onChange={setRecipValue}
              tokenSeparators={[",", " "]}
              maxTagCount="responsive"
              placeholder="Search the project's partners, or type any address"
              roster={recipRoster || []}
              personValue={recipientEmailValue}
            />
            <Button
              size="small"
              type="primary"
              loading={recipSaving}
              onClick={async () => {
                setRecipSaving(true);
                try {
                  await updateIssue(issue._id, {
                    recipients: (recipValue || []).map((e) => ({ email: String(e).trim().toLowerCase() })).filter((r) => r.email),
                  });
                  message.success("Recipients saved - they are mailed when this issue is approved");
                  loadAll();
                } catch (e) {
                  message.error(e.message || "Could not save the recipients");
                } finally {
                  setRecipSaving(false);
                }
              }}
            >
              Save
            </Button>
          </div>
        )}
        {/* RESEND DELIVERABLES in front of each saved recipient (owner,
            2026-10-30): mails that one person now, the deliverables
            attached - the approval mail only ever goes once. */}
        {isOwnerSide && (
          <RecipientResendList issue={issue} hasDeliverables={deliveredSlides.length > 0} onSent={loadAll} />
        )}
      </div>

      {/* DELIVERED - what answered the issue - above the MEDIA HISTORY of
          everything the issue collected. Both open on the canvas. */}
      {deliveredSlides.length > 0 && (
        <MediaHistoryStrip
          title="DELIVERED"
          accent={T.added2}
          slides={deliveredSlides}
          activeUrl={activeMediaUrl}
          onSelect={openMedia}
        />
      )}
      {(mediaSlides.length > 0 || canEdit) && (
        <MediaHistoryStrip
          title="MEDIA HISTORY"
          slides={mediaSlides}
          activeUrl={activeMediaUrl}
          onSelect={openMedia}
          onDelete={canDeleteMedia ? deleteMedia : null}
          onBack={viewVersion && currentVersion && viewVersion.url !== currentVersion.url ? () => { setViewVersion(null); chooseSurface("image"); } : null}
          right={canEdit ? (pasting ? "Adding pasted media..." : "Paste (Ctrl+V), attach, record or pick a sheet - every source medium lands here, newest first") : null}
        />
      )}

      {/* Media attached from the clipboard / Paste media (image, video, PDF, Excel) */}
      <IssueAttachmentsStrip issue={issue} canEdit={canEdit} me={userInfoRedux} guest={guest} onChanged={loadAll} />

      {/* EXTRA SCOPE, because the owner/GC had signed this scope off before
          the issue was raised (owner, 2026-10-21). Above the shop drawing
          pair, because the pair is the ANSWER to it: the chip says "this is a
          variation", and Link below is how the drawing that covers it gets
          attached. A mark nobody can see is not a mark. */}
      {issue && issue.extraScope && issue.extraScope.isExtra ? (
        <div style={{ margin: "8px 0" }} data-testid="issue-extra-scope">
          <ExtraScopeChip extraScope={issue.extraScope} />
        </div>
      ) : null}

      {/* The shop drawing this issue is paired with - one set of hours for
          both - with Link / Unlink for the project team. */}
      {!isGuest && issue?._id ? <IssueShopDrawingLink issue={issue} onChanged={loadAll} /> : null}

      {/* Fields carried over from an imported tracker / RFI log / minutes (renders nothing otherwise) */}
      <ImportedIssueDetails issue={issue} />

      {/* Which canvas — defaults to what the issue was raised on, and
          switching is remembered per issue. */}
      <SurfaceTabs
        surfaces={availableSurfaces(issue)}
        value={activeSurface}
        onChange={chooseSurface}
        note={activeSurface === "3d" && !modelAttached
          ? "pick a model version in the 3D MODEL selector to load one"
          : ""}
      />

      {/* Body — one of three layouts */}
      {fullscreen && (
        <style>{`.issue-body-grid.issue-fullscreen > :nth-child(2) { display: none !important; }`}</style>
      )}
      <div className={`issue-body-grid${fullscreen ? " issue-fullscreen" : ""}`} style={fullscreen ? {
        // inset:0 (not 100vh) so the overlay covers the whole viewport even
        // under the page zoom (vh units are zoomed, inset is not).
        position: "fixed", top: 0, right: 0, bottom: 0, left: 0, zIndex: 1500, background: "#fff", display: "grid",
        gridTemplateColumns: "1fr 380px", gridTemplateRows: "minmax(0, 1fr)", gap: 0, minHeight: 0,
      } : {
        display: "grid",
        // One primary column in every layout — the 3D pane no longer gets
        // its own grid column; it renders collapsed inside the primary
        // column (Collapsible3D) unless it's the sole source.
        gridTemplateColumns: `minmax(0, 1fr) ${colW.mid}px ${colW.right}px`,
        position: "relative",
        gap: 0,
        height: "calc(100 * var(--vh) - 170px)",
        minHeight: 520,
      }}>
        {activeSurface === "3d" && (
          <ThreeDBody
            issue={issue} canEdit={editableNow} userInfo={effectiveUserInfo}
            onIssueUpdated={loadAll}
            actionBar={actionBar}
            canvasActions={canvasActions} paneRef={primaryPaneRef}
            review3D={review3D}
            resolutionShot={resolutionShot} resolutionInfo={resolutionInfo}
            onShowShot={(u) => { showShotOnCanvas(u); chooseSurface("image"); }}
            screenshot={shownScreenshot}
            onSaveFields={saveLinkedFields} linkedOpts={linkedOpts}
          />
        )}
        {activeSurface === "video" && (
          <>
            <div ref={primaryPaneRef} style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", padding: "14px 16px 14px 20px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 8 }}>
                VIDEO ({paneVideos.length})
              </div>
              {actionBar}
              {canvasActions}
              <VideoPane videos={paneVideos} selectedUrl={mediaUrl} onSelect={setMediaUrl} fill />
            </div>
            <div style={{
              display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
              borderLeft: `1px solid ${T.line}`, background: "#fff", padding: "14px 16px", overflow: "auto",
            }}>
              {/* LINKED TO on every surface (owner, 2026-10-16) - it was missing here. */}
              <LinkedToBlock issue={issue} canEdit={editableNow && linkedOpts.canEditFields !== false} canPickContractor={linkedOpts.canPickContractor !== false} onSaveFields={saveLinkedFields} />
              <ResolutionShotBox
                shot={resolutionShot} fallback={shownScreenshot} info={resolutionInfo} hours={issue?.hoursSpent}
                maxHeight={200} onOpen={(u) => { showShotOnCanvas(u); chooseSurface("image"); }}
              />
              <ResolveChecklist
                hasScreenshot={!!(resolutionShot || shownScreenshot)}
                hasTaggedUsers={((issue.responsibleUsers || issue.assignedTo || []).length) > 0}
                policy={issue.evidencePolicy}
                hasDocument={(issue.attachments || []).some((a) => ["pdf", "excel", "doc", "document"].includes(a?.kind))}
                needsCommit={false}
              />
            </div>
          </>
        )}
        {activeSurface === "document" && (
          <>
            <div ref={primaryPaneRef} style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", padding: "14px 16px 14px 20px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 8 }}>
                DOCUMENT ({paneDocs.length})
              </div>
              {actionBar}
              {canvasActions}
              <IssueDocumentPane docs={paneDocs} selectedUrl={mediaUrl} onSelect={setMediaUrl} fill />
            </div>
            <div style={{
              display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
              borderLeft: `1px solid ${T.line}`, background: "#fff", padding: "14px 16px", overflow: "auto",
            }}>
              {/* LINKED TO on every surface (owner, 2026-10-16) - it was missing here. */}
              <LinkedToBlock issue={issue} canEdit={editableNow && linkedOpts.canEditFields !== false} canPickContractor={linkedOpts.canPickContractor !== false} onSaveFields={saveLinkedFields} />
              <ResolutionShotBox
                shot={resolutionShot} fallback={shownScreenshot} info={resolutionInfo} hours={issue?.hoursSpent}
                maxHeight={200} onOpen={(u) => { showShotOnCanvas(u); chooseSurface("image"); }}
              />
              <ResolveChecklist
                hasScreenshot={!!(resolutionShot || shownScreenshot)}
                hasTaggedUsers={((issue.responsibleUsers || issue.assignedTo || []).length) > 0}
                policy={issue.evidencePolicy}
                hasDocument={documents.length > 0}
                needsCommit={false}
              />
            </div>
          </>
        )}
        {!["3d", "video", "document"].includes(activeSurface) && layout === "V1" && (
          <V1Body
            onSaveFields={saveLinkedFields} linkedOpts={linkedOpts}
            issue={issue} canEdit={editableNow} surface={activeSurface}
            userInfo={effectiveUserInfo} onSave={handleSaveIssue}
            screenshot={shownScreenshot} onMarkupSave={handleMarkupSave} shotSaveRef={shotSaveRef}
            actionBar={actionBarWithCanvas} sheetSaveRef={sheetSaveRef} fullscreen={fullscreenCtl}
            resolutionShot={resolutionShot} resolutionInfo={resolutionInfo} onShowShot={showShotOnCanvas}
            review3D={review3D}
          />
        )}
        {!["3d", "video", "document"].includes(activeSurface) && layout === "V2" && (
          <V2Body
            onSaveFields={saveLinkedFields} linkedOpts={linkedOpts}
            issue={issue} canEdit={editableNow}
            userInfo={effectiveUserInfo} onIssueUpdated={loadAll}
            screenshot={shownScreenshot} shotSaveRef={shotSaveRef} fullscreen={fullscreenCtl}
            onMarkupSave={handleMarkupSave}
            actionBar={actionBarWithCanvas}
            resolutionShot={resolutionShot} resolutionInfo={resolutionInfo} onShowShot={showShotOnCanvas}
            review3D={review3D}
          />
        )}
        {!["3d", "video", "document"].includes(activeSurface) && layout === "V3" && (
          <V3Body
            onSaveFields={saveLinkedFields} linkedOpts={linkedOpts}
            issue={issue} canEdit={editableNow} surface={activeSurface}
            userInfo={effectiveUserInfo} sheetSaveRef={sheetSaveRef} shotSaveRef={shotSaveRef}
            onSave={handleSaveIssue}
            onIssueUpdated={loadAll}
            screenshot={shownScreenshot} fullscreen={fullscreenCtl}
            onMarkupSave={handleMarkupSave}
            actionBar={actionBarWithCanvas}
            resolutionShot={resolutionShot} resolutionInfo={resolutionInfo} onShowShot={showShotOnCanvas}
            review3D={review3D}
          />
        )}

        {/* Shared right rail — Activity + actions */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", background: "#fff", borderLeft: `1px solid ${T.line}` }}>
          <div style={{ padding: "14px 20px 8px", overflow: "auto", flex: 1, minHeight: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 10 }}>
              ACTIVITY ({activityEvents.length})
            </div>
            <ActivityThread events={activityEvents} compact={layout === "V3"} />
          </div>
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.line}`, background: layout === "V3" ? "#fff" : T.bg }}>
            {layout !== "V3" && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                {canResolve ? (
                  <Pill kind="added">✓ READY TO RESOLVE</Pill>
                ) : issue.status === "resolved" ? (
                  <Pill kind="added">✓ RESOLVED</Pill>
                ) : (
                  <Pill kind="orange">MISSING REQUIREMENTS</Pill>
                )}
                <span style={{ fontSize: 10.5, color: T.muted }}>
                  {layout === "V1" ? "2D-only · no model commit required" : `Linked to commit · milestone ${milestoneLabel || "—"}`}
                </span>
              </div>
            )}
            {issue.status === "resolved" && (issue.resolution?.evidence || []).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <EvidenceList items={issue.resolution.evidence} issue={issue} compact />
              </div>
            )}
            {/* Not yet approved: the proof-of-fix still lives on the
                proposal, so read it from there (nothing is on the issue
                until a reviewer approves). */}
            <ResolutionDiffStrip
              diff={resDiff}
              loading={resDiffLoading}
              reviewing={reviewing3D}
              canReview={canReviewProposals}
              onReview={() => setReviewing3D((v) => {
                const next = !v;
                // Reviewing a change means looking at it: bring the 3D
                // canvas up rather than painting a change onto a pane
                // nobody has open.
                if (next) chooseSurface("3d");
                return next;
              })}
              onEdits={() => setEditsOpen(true)}
            />
            <ModelEditsDrawer
              open={editsOpen}
              commitId={resCommitId}
              projectTitle={issue?.projectId?.title || ""}
              onClose={() => setEditsOpen(false)}
            />
            {/* Owner-side: the proposals endpoint is jwt-only. */}
            {!isGuest && <PendingProposalsPanel
              issueId={issue._id}
              canReview={canReviewProposals}
              onReviewed={loadAll}
            />}
            {candidateFix && candidateFix.candidateCount > 0 && (
              <div
                style={{
                  background: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  color: "#1E3A8A",
                  fontSize: 12,
                  padding: "6px 10px",
                  borderRadius: 6,
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                title="These candidates were auto-detected by diffing the model's opened-at snapshot against the latest commit."
              >
                <CheckCircleOutlined style={{ color: "#2563EB" }} />
                <span style={{ flex: 1 }}>
                  {candidateFix.candidateCount} element
                  {candidateFix.candidateCount === 1 ? "" : "s"} changed since this
                  issue was opened
                  {" — pre-selected in the viewer. Deselect any that weren't part of your fix."}
                </span>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: "auto", fontSize: 12 }}
                  onClick={() => {
                    try {
                      const v = activeIssueViewer.current?.viewer;
                      const store = v?.streaming?.state || v?.stateStore;
                      if (store?.clearIsolation) store.clearIsolation();
                      else if (store?.setIsolation) store.setIsolation(null);
                    } catch (e) { /* best-effort */ }
                  }}
                >
                  Show all
                </Button>
              </div>
            )}
            <Input.TextArea
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              placeholder={canEdit ? "Add a comment…" : "Read-only"}
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ marginBottom: 8 }}
              disabled={!canEdit && permission !== "comment"}
            />
            <div style={{ display: "flex", gap: 6 }}>
              <Button size="small" style={{ flex: 1 }} icon={<MessageOutlined />}
                onClick={handlePostComment} loading={posting}
                disabled={!commentDraft.trim() || (!canEdit && permission !== "comment")}>
                Comment
              </Button>
              <Button
                size="small" type="primary" icon={<CheckCircleOutlined />}
                onClick={openResolve}
                disabled={!canResolve || !canEdit}
                title={resolveHint}
                loading={resolving}
                style={{
                  flex: 1,
                  background: canResolve && canEdit ? T.added2 : undefined,
                  borderColor: canResolve && canEdit ? T.added2 : undefined,
                }}
              >
                Mark resolved →
              </Button>
            </div>
          </div>
        </div>
        {/* COLUMN HANDLES (owner, 2026-10-16: "make the issue view columns
            resizable with all content inside resize accordingly"). Drag the
            border between two columns; double-click puts the defaults back. */}
        {!fullscreen ? (
          <>
            <ColumnHandle right={colW.mid + colW.right} label="Resize the middle column"
              onDrag={(dx, start) => setCols({ mid: start.mid - dx, right: start.right })} start={colW} onReset={resetCols} />
            <ColumnHandle right={colW.right} label="Resize the right column"
              onDrag={(dx, start) => {
                // The border between the two rails moves only as far as BOTH
                // can follow, so one rail at its limit never squeezes the canvas.
                const lo = Math.max(COL_MIN - start.mid, start.right - COL_MAX);
                const hi = Math.min(COL_MAX - start.mid, start.right - COL_MIN);
                const d = Math.max(lo, Math.min(hi, dx));
                setCols({ mid: start.mid + d, right: start.right - d });
              }} start={colW} onReset={resetCols} />
          </>
        ) : null}
      </div>
    </div>
  );
}

// ─── Canvas actions ──────────────────────────────────────────────────────
//
// The same three affordances on every surface, because "look at it" and
// "mark it up" are the same job whatever is on the canvas:
//
//   Fullscreen      the pane fills the window
//   Capture         whatever the canvas is showing right now - a 3D view,
//                   a video frame, a PDF page - becomes a new image
//                   version and the canvas switches to it, ready to draw
//                   on. This is the "take a screenshot for markup" the
//                   3D pane never had.
//   Save markup     writes the annotated raster back as a new version
//   Camera          take a picture or record a video here and now
//
// Save is disabled where there is nothing to save (a 3D view is not an
// annotated raster until you capture it first).
function CanvasActions({ fullscreen, onCapture, onSave, onCamera, canEdit, capturing, saving, canSave, canCapture, captureLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
      <Button size="small" icon={<ExpandOutlined />} onClick={fullscreen?.toggle}>
        {fullscreen?.active ? "Exit fullscreen" : "Fullscreen"}
      </Button>
      {canEdit && (
        <Tooltip title={canCapture
          ? "Put what the canvas is showing on a new image version and open it for markup"
          : "This canvas already IS the markup surface — draw on it and use Save markup"}>
          <span>
            <Button size="small" icon={<CameraOutlined />} loading={capturing} disabled={!canCapture} onClick={onCapture}>
              {captureLabel || "Capture for markup"}
            </Button>
          </span>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip title={canSave ? "Save the markup as a new image version" : "Capture the view first — there is no markup layer on this surface"}>
          <span>
            <Button size="small" icon={<SaveOutlined />} loading={saving} disabled={!canSave} onClick={onSave}>
              Save markup
            </Button>
          </span>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip title="Use a camera on this device — take a picture or record a video">
          <Button size="small" icon={<VideoCameraOutlined />} onClick={onCamera}>
            Camera
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

// ─── Surface switcher ────────────────────────────────────────────────────
//
// Which canvas the issue is showing. The default comes from what the
// issue was raised on (helpers/issueSurface), and the choice is
// remembered per issue so a background refresh never pulls a reviewer
// out of the model.
const SURFACE_LABEL = { sheet: "2D sheet", image: "Image / markup", "3d": "3D model", video: "Video", document: "Document" };

function SurfaceTabs({ surfaces, value, onChange, note }) {
  if (!surfaces || surfaces.length < 2) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6, padding: "8px 24px",
      background: "#fff", borderBottom: `1px solid ${T.line}`, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginRight: 4 }}>
        SHOWING
      </span>
      {surfaces.map((s) => {
        const on = s === value;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            style={{
              border: `1px solid ${on ? T.navy : T.line}`,
              background: on ? T.navy : "#fff",
              color: on ? "#fff" : T.text,
              borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer",
              fontWeight: on ? 600 : 400,
            }}
          >
            {SURFACE_LABEL[s] || s}
          </button>
        );
      })}
      {note && <span style={{ fontSize: 11, color: T.muted, marginLeft: 4 }}>{note}</span>}
    </div>
  );
}

// ─── Video pane ──────────────────────────────────────────────────────────
// A site video is evidence like any other; it used to be reachable only
// as a link in the attachments strip.
function VideoPane({ videos, selectedUrl, onSelect, fill = true, height }) {
  const v = videos.find((x) => x.url === selectedUrl) || videos[0];
  const shell = {
    position: "relative", background: "#111", border: "1px solid #0C0C0C",
    borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column",
    ...(fill ? { flex: 1, minHeight: 0 } : { height: height || 360 }),
  };
  if (!v) {
    return <div style={{ ...shell, alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.6)", fontSize: 12 }}>No video attached.</div>;
  }
  return (
    <div style={shell}>
      {/* autoPlay so a video pasted or recorded onto the issue starts on
          its own; browsers block unmuted autoplay, and when they do the
          controls are right there - which is better than muting someone
          else's site recording by default. */}
      <video
        key={v.url}
        src={toImgSrc(v.url)}
        controls
        autoPlay
        playsInline
        style={{ flex: 1, minHeight: 0, width: "100%", background: "#000", objectFit: "contain" }}
      />
      {videos.length > 1 && (
        <div style={{ display: "flex", gap: 6, padding: 8, background: "#15181B", overflowX: "auto" }}>
          {videos.map((x, i) => (
            <button
              key={x.url}
              type="button"
              onClick={() => onSelect && onSelect(x.url)}
              style={{
                border: `1px solid ${x.url === v.url ? T.navy : "#2A3036"}`,
                background: x.url === v.url ? "#22303A" : "#1A1D21",
                color: "#D7DDE2", borderRadius: 4, fontSize: 11, padding: "3px 8px", cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {x.name || `Video ${i + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── 3D body — the model canvas as the primary surface ───────────────────
// Same right-hand rail as the other layouts (diff, resolution screenshot,
// checklist) so switching surface changes what you look at, not what you
// can do.
function ThreeDBody({
  issue, canEdit, userInfo, onIssueUpdated, actionBar, review3D,
  resolutionShot, resolutionInfo, onShowShot, screenshot, canvasActions, paneRef,
  onSaveFields = null, linkedOpts = {},
}) {
  const commitSha = String(issue.resolution?.commitSha || issue.sourceModel?.commitSha || "").slice(0, 7);
  return (
    <>
      <div ref={paneRef} style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        padding: "14px 16px 14px 20px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
            {modelSourceLabel(issue)}
          </span>
          {issue.sourceModel?.name && <Pill>{issue.sourceModel.name}</Pill>}
          {commitSha && <Pill kind="navy">commit {commitSha}</Pill>}
        </div>
        {actionBar}
        {canvasActions}
        <IssueModelCanvas
          issue={issue} canEdit={canEdit} userInfo={userInfo}
          onIssueUpdated={onIssueUpdated} fill
          review={review3D}
        />
      </div>

      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        borderLeft: `1px solid ${T.line}`, background: "#fff",
        padding: "14px 16px", overflow: "auto",
      }}>
        {/* LINKED TO on the 3D surface too (owner, 2026-10-16) - it never showed here. */}
        <LinkedToBlock issue={issue} canEdit={canEdit && linkedOpts.canEditFields !== false} canPickContractor={linkedOpts.canPickContractor !== false} onSaveFields={onSaveFields} />
        <ResolutionShotBox
          shot={resolutionShot} fallback={screenshot} info={resolutionInfo} hours={issue?.hoursSpent}
          dark maxHeight={190} onOpen={onShowShot}
        />
        <ResolveChecklist
          hasScreenshot={!!(resolutionShot || screenshot)}
          hasTaggedUsers={((issue.responsibleUsers || issue.assignedTo || []).length) > 0}
          policy={issue.evidencePolicy}
          hasDocument={(issue.attachments || []).some((a) => ["pdf", "excel", "doc", "document"].includes(a?.kind) || /\.(pdf|xlsx?|xlsm|csv|docx?)(\?|$)/i.test(a?.url || a?.name || ""))}
          hasCommit={!!commitSha}
          needsCommit
        />
      </div>
    </>
  );
}

// Every tag on the issue, the one on the title first. Old issues carry only
// titleTag; newer ones the whole list in `tags` (back/utils/issueTitleTag).
export function issueTags(issue) {
  const out = [];
  for (const t of [issue && issue.titleTag, ...((issue && issue.tags) || [])]) {
    const v = String(t || "").trim().toUpperCase();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

// The companies an issue can be given to: the owning company and each active
// partner, under the header every picker shows ("BES - Electrical"). The
// current one stays listed by name even if it has left the project.
function contractorOptions(roster, currentId, currentName) {
  const opts = (roster || []).filter((g) => g && g.companyId).map((g) => ({
    value: String(g.companyId),
    label: g.role === "owner" ? `${g.companyName || "Owner"} (owner)` : (g.label || g.companyName || "Partner"),
  }));
  if (currentId && !opts.some((o) => o.value === String(currentId))) {
    opts.push({ value: String(currentId), label: currentName || "A company no longer on this project" });
  }
  return opts;
}

// ─── V1 body — 2D only (sheet fills, side rail holds screenshot + checklist)
// LINKED TO — revision, milestone, commit, diff, project, where the issue
// is (level / area / grid / discipline). Every layout shows it: it used to
// live only in the 2D+3D body, so an issue raised on a sheet alone, or in
// 3D alone, never showed what it was linked to.
// EDITABLE IN PLACE, FOUR ROWS OF ELEVEN. Owner, 2026-10-01: "add only
// LINKED TO edit option, inside exiting view". The page settled which reading
// that was - this block is literally headed "LINKED TO" and was entirely
// read-only, and four of its rows were four of the twelve fields with no edit
// path anywhere in the app.
//
// WHICH FOUR, AND WHY NOT THE REST:
//   level, area, grid, discipline  - nothing could change them. Editable.
//   Sheet, Revision, Project       - have their own routes already. Left as
//                                    read-outs rather than given a second way.
//   Commit, Diff, Cost impact      - DERIVED. Owner the same day: "leave the
//                                    cost as is now, we can not make that
//                                    editable".
//   Milestone                      - read-only, and not because nobody got to
//                                    it: `milestone` is not a field on the
//                                    issue model at all and NOT ONE of the 414
//                                    prod issues has it. The row can only ever
//                                    show a dash. Naming it rather than
//                                    inventing a write for it.
//
// THE `custom` FLAG IS LEFT ALONE, DELIBERATELY. models/issue carries it so a
// value TYPED rather than PICKED from the project's own levels and areas is
// kept and flagged. There is no location-options endpoint for the client yet,
// so this editor cannot tell the two apart - and flagging every save as typed
// would make the flag mean "edited here" instead of "not in the project's
// list". It writes the value and says nothing about provenance. A picker needs
// that endpoint and is a separate job.
// EXPORTED FOR TEST ONLY. The page itself needs a dozen mocks to mount,
// and the bug that crashed it lives entirely in this block's own event
// handling - so the regression test drives the block directly rather
// than paying for the page.
//
// DISCIPLINE, CONTRACTOR AND TAGS (owner, 2026-10-15: "fields
// dicipline/contractor and tag should be visible inside each issue", and
// "issues, can be tagged by multiple tags"). Three rows every reader sees;
// editing uses the same pickers as bulk "Set fields" - the discipline with
// each contractor under it (components/Issue/DisciplineContractorPicker),
// the contractor on its own, and the tags with the project's tags offered.
// What those pickers list is loaded only once somebody presses Edit.
// `canPickContractor` is false for a shared link: the server refuses a
// contractor change from one, so the row stays a read-out there.
// The activities the server named for this issue (GET: issue.activities),
// or the one activity of an older payload.
function issueActivitiesOf(issue) {
  if (Array.isArray(issue.activities) && issue.activities.length) return issue.activities;
  return issue.activity ? [issue.activity] : [];
}
// The leaves to offer, plus every activity the issue already has that the
// list no longer offers (deleted / not a leaf) - shown, disabled, by name.
function activitiesPickOptions(activities, currentIds, known) {
  const opts = leafActivityOptions(activities, { offer: (it) => !it.disabled });
  const onPlan = new Map((activities || []).map((it) => [String(it._id || it.id), it]));
  for (const id of currentIds || []) {
    if (opts.some((o) => o.value === String(id))) continue;
    const it = onPlan.get(String(id));
    const a = it || (known || []).find((k) => String(k._id) === String(id));
    if (!a) continue;
    const note = it ? (it.disabled ? "disabled" : NOT_LEAF_NOTE) : "no longer on the schedule";
    opts.unshift(leafOption({ value: String(id), name: a.name || "(unnamed)", path: a.path || [], disabled: true, note }));
  }
  return opts;
}

// The estimate with a "Change" link for estimateEdit holders (PUT /issue/:id/estimate).
function EstimateInline({ issue, shown, onSaved, inline = true }) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(shown);
  const [busy, setBusy] = React.useState(false);
  const text = shown != null ? `${shown} h` : "—";
  if (!issue.canEditEstimate || !inline) return text;
  if (!open) {
    return (
      <span>
        {text}
        <Button type="link" size="small" style={{ padding: "0 4px", height: 18 }} aria-label="change estimated hours"
                onClick={() => { setValue(shown); setOpen(true); }}>Change</Button>
      </span>
    );
  }
  const save = async () => {
    if (!(Number(value) > 0)) { message.warning("Estimated hours must be a number greater than 0"); return; }
    setBusy(true);
    try {
      const r = await setIssueEstimate(issue._id, Number(value));
      onSaved(r && r.estimatedHours != null ? r.estimatedHours : Number(value));
      message.success("Estimate saved");
      setOpen(false);
    } catch (e) {
      message.error(e.message || "Could not save the estimate");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <InputNumber size="small" aria-label="estimated hours inline" min={0.25} step={0.25} precision={2}
        style={{ width: 90 }} value={value} onChange={(v) => setValue(v)} />
      <Button size="small" type="primary" loading={busy} onClick={save}>Save</Button>
      <Button size="small" onClick={() => setOpen(false)}>Cancel</Button>
    </span>
  );
}

// The projected cost line: estimated hours x the contractor's rate, as
// GET /issue/id/:id priced it (utils/estimateProjection). No projection = the
// reader may not see cost, or there is no estimate: a dash, never "$0".
/** A drag handle on a column border, `right` px from the grid's right edge. */
function ColumnHandle({ right, label, onDrag, onReset, start }) {
  const [active, setActive] = React.useState(false);
  const begin = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    // The app runs under a page zoom: pointer moves are screen pixels, the
    // grid's widths are CSS pixels. Measured, not assumed.
    const el = e.currentTarget;
    const zoom = (el.getBoundingClientRect().width / (el.offsetWidth || 1)) || 1;
    const s0 = { ...start };
    setActive(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    let frame = 0;
    const move = (ev) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => onDrag((ev.clientX - x0) / zoom, s0));
    };
    const up = () => {
      setActive(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  return (
    <div
      role="separator" aria-orientation="vertical" aria-label={label} title={`${label} - drag; double-click for the default`}
      onMouseDown={begin} onDoubleClick={onReset}
      style={{
        position: "absolute", top: 0, bottom: 0, right: right - 4, width: 8, cursor: "col-resize", zIndex: 6,
        background: active ? "rgba(22,119,255,0.25)" : "transparent",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(22,119,255,0.15)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    />
  );
}

function projectedCostText(pj, hours) {
  if (!pj) return "—";
  const h = Number(hours);
  if (!(h > 0)) return "—";
  if (pj.rateSource === "no-cost") return "No cost";
  if (pj.rate == null) return `No rate set${pj.companyName ? ` for ${pj.companyName}` : ""}`;
  const cost = Math.round(h * pj.rate * 100) / 100;
  return `$${cost.toLocaleString()} (${h} h × $${pj.rate}/h)`;
}

// THE TITLE, EDITABLE (owner, 2026-10-19: "edit issue doesn't allow the
// title edit, the title should become clickable/editable in edit mode").
// For whoever may edit the issue's fields: click the title (or the pencil),
// change it, Enter or Save. A SNAG-style tag in front is kept by the server
// (utils/issueTitleTag), so it is offered here as part of the text.
export function IssueTitle({ title, canEdit = false, onSave = null }) {
  const [draft, setDraft] = React.useState(null);   // null = not editing
  const [saving, setSaving] = React.useState(false);
  const editable = canEdit && !!onSave;
  const start = () => { if (editable && draft === null) setDraft(title || ""); };
  const save = async () => {
    const next = String(draft || "").replace(/\s+/g, " ").trim();
    if (!next) { message.warning("The title cannot be empty"); return; }
    if (next === String(title || "").trim()) { setDraft(null); return; }
    setSaving(true);
    try { await onSave(next); setDraft(null); } catch (e) { /* the page said why; keep the draft */ } finally { setSaving(false); }
  };
  if (draft !== null) {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <Input.TextArea autoFocus autoSize={{ minRows: 1, maxRows: 4 }} value={draft} maxLength={500}
                        aria-label="Issue title" disabled={saving}
                        style={{ fontSize: 16, fontWeight: 600 }}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); }
                          if (e.key === "Escape") setDraft(null);
                        }} />
        <Button type="primary" size="small" loading={saving} onClick={save}>Save</Button>
        <Button size="small" disabled={saving} onClick={() => setDraft(null)}>Cancel</Button>
      </div>
    );
  }
  return (
    <div style={{ fontSize: 18, fontWeight: 600, color: T.ink, letterSpacing: "-.005em", cursor: editable ? "text" : undefined }}
         onClick={start} title={editable ? "Click to edit the title" : undefined} data-testid="issue-title">
      {title || "Untitled issue"}
      {editable ? (
        <Tooltip title="Edit the title">
          <EditOutlined aria-label="Edit the title" style={{ marginLeft: 8, fontSize: 14, color: T.muted }}
                        onClick={(e) => { e.stopPropagation(); start(); }} />
        </Tooltip>
      ) : null}
    </div>
  );
}

export function LinkedToBlock({ issue, bordered = false, canEdit = false, onSaveFields = null, canPickContractor = true }) {
  const commitSha = String(issue.resolution?.commitSha || issue.sourceModel?.commitSha || "").slice(0, 7);
  const diff = issue.diffSummary || {};
  const [draft, setDraft] = React.useState(null);   // null = not editing
  const [estShown, setEstShown] = React.useState(null); // an estimate saved inline, until the page reloads
  const [saving, setSaving] = React.useState(false);
  const editable = canEdit && !!onSaveFields;
  const current = {
    level: issue.location?.level || "",
    area: issue.location?.area || "",
    // Several levels / areas (owner, 2026-10-16: "same fix for the issue edit
    // location") - the lists when there are some, else the one value.
    levels: issue.location?.levels?.length ? issue.location.levels : (issue.location?.level ? [issue.location.level] : []),
    areas: issue.location?.areas?.length ? issue.location.areas : (issue.location?.area ? [issue.location.area] : []),
    grid: issue.location?.grid || "",
    system: issue.location?.system || "",
    // Several systems (owner, 2026-11-04: "we need systems to also support
    // multiple values") - the list when there is one, else the one system.
    systems: issue.location?.systems?.length ? issue.location.systems : (issue.location?.system ? [issue.location.system] : []),
    discipline: issue.discipline || "",
    responsibleCompanyId: issue.responsibleCompanyId || "",
    tags: issueTags(issue),
    ganttItemId: String(issue.ganttItemId?._id || issue.ganttItemId || ""),
    // Every activity, first one first (several, all equal - owner 2026-10-16).
    ganttItemIds: issueActivityIds(issue),
    estimatedHours: issue.estimatedHours == null ? null : Number(issue.estimatedHours),
  };
  const [pickers, setPickers] = React.useState({ roster: [], disciplines: [], tags: [], activities: [] });
  // Trades beneath the contractor, partners beneath the discipline, in read
  // mode too (owner, 2026-10-16) - the shared, cached project roster.
  const partnerTrades = usePartnerTrades(issue.projectId?._id || issue.projectId);

  // THE SYSTEMS ARE PICKED WITH THE SAME CONTROL AS THE LEVELS AND AREAS.
  //
  // Owner, 2026-10-06: a system dropdown "just like area/level", on creation
  // AND on the issue page, "that can take new entries, which will be saved
  // and added to the full dropdown list of systems". That was a text box with
  // a datalist until 2026-11-04, when the owner asked for SEVERAL systems
  // like tags, levels and areas - and a datalist cannot hold several. It is a
  // LocationPicker now, which is where the project's systems are offered for
  // every other form (one list, grouped into the tracked vocabulary and what
  // people added, still accepting anything typed), so this page cannot come
  // to offer a different list from New issue.
  React.useEffect(() => {
    if (!draft) return undefined;        // readers of the page ask for nothing
    let alive = true;
    const pid = issue.projectId?._id || issue.projectId;
    // The roster (partners and their trades), the owning company's
    // disciplines and the project's tags - each on its own, so one failing
    // still leaves the other two pickers working.
    const owner = String(issue.companyId?._id || issue.companyId || "");
    // The schedule too (owner, 2026-10-16: the activity "in linked to on the
    // issue view, where we can change it in edit mode") - the WHOLE schedule,
    // so a leaf can be told from a parent; only live leaves are offered.
    Promise.all([
      pid ? getParticipantsRoster(pid).catch(() => []) : Promise.resolve([]),
      owner ? getDiscipline(owner).catch(() => []) : Promise.resolve([]),
      pid ? getIssueTags(pid).catch(() => ({ tags: [] })) : Promise.resolve({ tags: [] }),
      pid && canPickContractor ? listGanttItems(pid).catch(() => []) : Promise.resolve([]),
    ]).then(([roster, disciplines, tags, gantt]) => {
      if (!alive) return;
      const all = Array.isArray(gantt) ? gantt : (gantt?.items || []);
      setPickers({
        roster: Array.isArray(roster) ? roster : [],
        disciplines: Array.isArray(disciplines) ? disciplines : (disciplines?.data || []),
        tags: (tags?.tags || []).map((t) => ({ value: t.tag, label: `${t.tag}  ·  ${t.count}` })),
        activities: withActivityPaths(all),
      });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft ? 1 : 0, String(issue.projectId?._id || issue.projectId || "")]);
  const save = async () => {
    // ESTIMATED HOURS (owner, 2026-10-16): once an issue has an estimate it
    // keeps one - emptied or zero is refused here, as the server would.
    const estChanged = draft.estimatedHours !== current.estimatedHours;
    if (estChanged && !(Number(draft.estimatedHours) > 0)) {
      message.error("Enter the estimated hours (more than 0)");
      return;
    }
    setSaving(true);
    try {
      // The contractor and the tags only when they changed: re-sending an
      // unchanged contractor that has since left the project would refuse
      // the whole save over a field nobody touched.
      const same = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
      await onSaveFields({
        location: {
          level: draft.level, area: draft.area, grid: draft.grid, system: draft.system,
          // Only when changed: an untouched list goes back as it was anyway.
          ...(!same(draft.levels, current.levels) ? { levels: draft.levels } : {}),
          ...(!same(draft.areas, current.areas) ? { areas: draft.areas } : {}),
          ...(!same(draft.systems, current.systems) ? { systems: draft.systems } : {}),
        },
        discipline: draft.discipline,
        ...(draft.responsibleCompanyId !== current.responsibleCompanyId ? { responsibleCompanyId: draft.responsibleCompanyId } : {}),
        ...(!same(draft.tags, current.tags) ? { tags: draft.tags } : {}),
        // Only when it changed, for the same reason as the contractor: an
        // activity since disabled would refuse a save that never touched it.
        ...(!same(draft.ganttItemIds, current.ganttItemIds) ? { ganttItemIds: draft.ganttItemIds } : {}),
        ...(estChanged ? { estimatedHours: Number(draft.estimatedHours) } : {}),
      });
      setDraft(null);
    } finally {
      setSaving(false);
    }
  };
  // THE LAST PLAIN TEXT BOX IN THIS BLOCK WAS THE SYSTEM, and it became a
  // picker on 2026-11-04 (several systems; a datalist cannot hold several),
  // so the `field` helper that made these inputs is gone with it. The lesson
  // it carried is worth keeping for the next onChange written here: React is
  // 16.14 and SyntheticEvent is POOLED, so read e.target.value into a
  // variable BEFORE the setDraft updater - the updater runs when React
  // flushes, by which time the event is back in the pool with target nulled.
  // That was a real crash (owner, 2026-10-02: "when editing issues linked to,
  // levels and area issue page crashes").
  return (
    <div style={bordered ? { padding: "14px 16px", borderBottom: `1px solid ${T.line}` } : { marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>LINKED TO</div>
        {editable && !draft ? (
          <button type="button" onClick={() => setDraft(current)}
                  style={{ fontSize: 10.5, color: T.muted, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            Edit
          </button>
        ) : null}
        {editable && draft ? (
          <>
            <button type="button" onClick={save} disabled={saving}
                    style={{ fontSize: 10.5, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={() => setDraft(null)} disabled={saving}
                    style={{ fontSize: 10.5, color: T.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Cancel
            </button>
          </>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, lineHeight: 1.3, color: T.text }}>
        {[
          ["Sheet", issue.sourceDocument?.sheetNumber || issue.sourceDocument?.name || "—"],
          ["Revision", `R${issue.sourceDocument?.revisionNumber || issue.createdRev || "—"}`],
          ["Milestone", issue.milestone?.value || issue.milestone?.key || "—"],
          ["Commit", commitSha || "—"],
          ["Diff", diff.componentsModified != null
            ? `${diff.componentsAdded || 0} added · ${diff.componentsModified} modified · ${diff.componentsRemoved || 0} removed`
            : "—"],
          ["Project", issue.projectId?.title || issue.projectId || "—"],
          // THE SCHEDULE ACTIVITY: its name, the path above it beneath -
          // the same two lines every activity picker shows. Edit offers
          // the lowest activities only (helpers/schedule/leafActivityOptions).
          ["Activity", draft && canPickContractor ? (
            // SEVERAL, ALL EQUAL (owner, 2026-10-16: generic issues "for
            // level1 and level2" - e.g. Level 1 › Overall + Level 2 › Overall).
            <Select filterOption={matchOption} size="small" mode="multiple" allowClear {...LEAF_SELECT_PROPS} style={{ width: "100%" }} aria-label="activity"
              placeholder="—" value={draft.ganttItemIds}
              options={activitiesPickOptions(pickers.activities, draft.ganttItemIds, issueActivitiesOf(issue))}
              onChange={(v) => setDraft((d) => ({ ...d, ganttItemIds: (v || []).filter(Boolean) }))} />
          ) : (issueActivitiesOf(issue).length ? (
            <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {issueActivitiesOf(issue).map((a) => (
                <span key={a._id} data-testid="issue-activity" title={[...(a.path || []), a.name].join(PATH_SEP)}
                  style={{ display: "flex", flexDirection: "column" }}>
                  <span>{a.name || (a.deleted ? "An activity no longer on the schedule" : "(unnamed)")}{a.name && a.deleted ? " - deleted from the schedule" : a.disabled ? " - disabled" : ""}</span>
                  {(a.path || []).length ? (
                    <span style={{ fontSize: 10.5, color: T.muted }}>{a.path.join(PATH_SEP)}</span>
                  ) : null}
                </span>
              ))}
            </span>
          ) : (current.ganttItemId ? "An activity no longer on the schedule" : "—"))],
          // The estimate the contractor cost page projects cost from - set or
          // corrected by whoever may pick the activity and the contractor.
          // ITS OWN GRANT (owner, 2026-10-16): estimateEdit, sent by the server
          // as issue.canEditEstimate - everyone else reads it.
          ["Est. hours", draft && issue.canEditEstimate ? (
            <InputNumber size="small" aria-label="estimated hours" min={0.25} step={0.25} precision={2}
              style={{ width: 110 }} placeholder="—" value={draft.estimatedHours}
              onChange={(v) => setDraft((d) => ({ ...d, estimatedHours: v == null ? null : Number(v) }))} />
          ) : (
            // ITS OWN SMALL EDITOR (owner, 2026-10-16): whoever holds
            // estimateEdit changes the estimate here even when they may not
            // edit the rest of the issue - a responsible partner, typically.
            // Only for those who cannot open Edit above (owner, 2026-10-16:
            // "why ... a button that says change ... and not allow the edit
            // from the same top edit place") - with Edit, it is edited there.
            <EstimateInline issue={issue} inline={!editable} shown={estShown != null ? estShown : current.estimatedHours} onSaved={setEstShown} />
          )],
          // PROJECTED COST (owner, 2026-10-16), where "Cost impact" sat - a
          // field nothing ever filled. The rate comes priced from the server
          // (the cost report's own rule); the hours follow the edit as typed.
          ["Projected cost", projectedCostText(issue.projection, draft ? draft.estimatedHours : current.estimatedHours)],
          // THE SAME PICKERS AS NEW ISSUE (owner, 2026-10-16): the project's
          // levels and areas to click, several of each, Overall first -
          // was a bare text box with nothing to pick from.
          ["Level", draft ? (
            <LocationPicker projectId={issue.projectId?._id || issue.projectId} fields={["level"]} multiLevel allTags fill size="small"
              value={draft} onChange={(v) => setDraft((d) => ({ ...d, level: v.level || "", levels: v.levels || [] }))} />
          ) : ((issue.location?.levels && issue.location.levels.length > 1 ? issue.location.levels.join(", ") : issue.location?.level) || "—")],
          ["Area", draft ? (
            <LocationPicker projectId={issue.projectId?._id || issue.projectId} fields={["area"]} multiArea allTags fill size="small"
              value={draft} onChange={(v) => setDraft((d) => ({ ...d, area: v.area || "", areas: v.areas || [] }))} />
          ) : ((issue.location?.areas && issue.location.areas.length > 1 ? issue.location.areas.join(", ") : issue.location?.area) || "—")],
          // SEVERAL GRID LOCATIONS (owner, 2026-10-18): the same tag picker
          // as New issue - I-40, L-50, read in either order.
          ["Grid", draft ? (
            <LocationPicker projectId={issue.projectId?._id || issue.projectId} fields={["grid"]} allTags fill size="small"
              value={draft} onChange={(v) => setDraft((d) => ({ ...d, grid: v.grid || "" }))} />
          ) : (issue.location?.grid || "—")],
          // SEVERAL SYSTEMS (owner, 2026-11-04), the same picker as the
          // levels and the areas above - and, read-only, every one of them.
          ["System", draft ? (
            <LocationPicker projectId={issue.projectId?._id || issue.projectId} fields={["system"]} multiSystem allTags fill size="small"
              value={draft} onChange={(v) => setDraft((d) => ({ ...d, system: v.system || "", systems: v.systems || [] }))} />
          ) : ((issue.location?.systems?.length ? issue.location.systems.join(", ") : issue.location?.system) || "—")],
          // SIGNED OFF BY OWNER/GC (owner, 2026-10-21): read from the GC's
          // sign-off of the level / area / system / activity it sits in - not
          // edited here; it is switched on the Levels, Areas & Systems page.
          ["Owner/GC", issue.ownerSignOff ? (
            <span data-testid="issue-owner-signoff" style={{ color: issue.ownerSignOff.planned ? "#3b82f6" : "#1d4ed8", fontWeight: 600 }}>
              ★ {issue.ownerSignOff.planned ? "Sign-off planned" : "Signed off by owner/GC"} {issue.ownerSignOff.date}
              <span style={{ color: T.muted, fontWeight: 400 }}> · {issue.ownerSignOff.scope}{issue.ownerSignOff.by ? ` · marked by ${issue.ownerSignOff.by}` : ""}</span>
            </span>
          ) : "Not signed off"],
          ["Discipline", draft ? (
            <DisciplineContractorPicker size="small" disciplines={pickers.disciplines} roster={pickers.roster}
              placeholder="—" value={draft.discipline ? { discipline: draft.discipline, responsibleCompanyId: draft.responsibleCompanyId } : null}
              onChange={(pick) => setDraft((d) => ({
                ...d,
                discipline: pick ? pick.discipline : "",
                ...(pick && pick.responsibleCompanyId && canPickContractor ? { responsibleCompanyId: pick.responsibleCompanyId } : {}),
              }))} />
          ) : (issue.discipline
            ? <TradeWithPartners label={issue.discipline} partners={partnerTrades.partnersOf(issue.discipline)} />
            : "—")],
          ["Contractor", draft && canPickContractor ? (
            <Select filterOption={matchOption} size="small" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} aria-label="contractor"
              placeholder="—" value={draft.responsibleCompanyId || undefined}
              options={contractorOptions(pickers.roster, draft.responsibleCompanyId, issue.responsibleCompanyName)}
              onChange={(v) => setDraft((d) => ({ ...d, responsibleCompanyId: v || "" }))} />
          ) : (issue.responsibleCompanyName
            ? <PartnerWithTrades name={issue.responsibleCompanyName} trades={partnerTrades.tradesOf(issue.responsibleCompanyId)} />
            : (issue.responsibleCompanyId ? "A company no longer on this project" : "—"))],
          ["Tags", draft ? (
            <Select size="small" mode="tags" allowClear style={{ width: "100%" }} aria-label="tags" tokenSeparators={[","]}
              placeholder="—" value={draft.tags} options={pickers.tags}
              onChange={(v) => setDraft((d) => ({ ...d, tags: (v || []).map((t) => String(t).trim().toUpperCase()).filter(Boolean) }))} />
          ) : (current.tags.length ? (
            <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {current.tags.map((t, i) => (
                <span key={t} data-testid="issue-tag" title={i === 0 ? "On the title" : undefined}
                  style={{ fontSize: 10.5, padding: "0 6px", borderRadius: 3, border: `1px solid ${T.line}`, background: i === 0 ? "#f5f5f5" : "#fff" }}>
                  {t}
                </span>
              ))}
            </span>
          ) : "—")],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex" }}>
            <span style={{ color: T.muted, width: 88, flexShrink: 0 }}>{k}</span>
            {/* String(v) would print "[object Object]" for the inputs above -
                the rows are nodes now, not only text. */}
            <span style={{ flex: 1, wordBreak: "break-word", display: "flex" }}>
              {typeof v === "object" && v !== null ? v : String(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// The 2D issue used to draw its sheet no matter which surface was
// chosen, so a picture picked from the media history, the DELIVERED row
// or the rail thumbnail changed nothing on screen. The surface decides
// now: "image" puts the picked picture on the markup canvas (the sheet
// is one click away), anything else is the sheet.
function V1Body({ issue, canEdit, onSaveFields, linkedOpts = {}, userInfo, onSave, actionBar, sheetSaveRef, shotSaveRef, fullscreen, resolutionShot, resolutionInfo, onShowShot, surface, screenshot: pickedShot, onMarkupSave }) {
  const screenshot = pickedShot || issue.thumbnail || issue.resolution?.screenshot;
  const showImage = surface === "image" && !!screenshot;
  return (
    <>
      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        padding: "14px 16px 14px 20px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
            {showImage ? "SOURCE · WHERE ISSUE WAS RAISED" : "2D SHEET"}
          </span>
          {issue.sourceDocument?.sheetNumber && <Pill>{issue.sourceDocument.sheetNumber}</Pill>}
        </div>
        {actionBar}
        {showImage ? (
          <ScreenshotMarkupPane
            issue={issue} canEdit={canEdit} userInfo={userInfo}
            onSave={onMarkupSave} screenshot={screenshot} fill
            saveRef={shotSaveRef} fullscreen={fullscreen}
          />
        ) : (
          <SheetPane issue={issue} canEdit={canEdit} userInfo={userInfo} onSave={onSave} fill saveRef={sheetSaveRef} fullscreen={fullscreen} />
        )}
      </div>

      {/* Side rail column — linked-to + screenshot + checklist */}
      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        borderLeft: `1px solid ${T.line}`, background: "#fff",
        padding: "14px 16px", overflow: "auto",
      }}>
        <LinkedToBlock issue={issue} canEdit={canEdit && linkedOpts.canEditFields !== false} canPickContractor={linkedOpts.canPickContractor !== false} onSaveFields={onSaveFields} />
        <ResolutionShotBox
          shot={resolutionShot} fallback={screenshot} info={resolutionInfo} hours={issue?.hoursSpent}
          maxHeight={240} onOpen={onShowShot}
        />

        <ResolveChecklist
          hasScreenshot={!!(resolutionShot || screenshot)}
          hasTaggedUsers={((issue.responsibleUsers || issue.assignedTo || []).length) > 0}
          policy={issue.evidencePolicy}
          hasDocument={(issue.attachments || []).some((a) => ["pdf", "excel", "doc", "document"].includes(a?.kind) || /\.(pdf|xlsx?|xlsm|csv|docx?)(\?|$)/i.test(a?.url || a?.name || ""))}
          needsCommit={false}
        />
      </div>
    </>
  );
}

// ─── V2 body — 3D-sourced. Screenshot is primary whenever one exists; the
// 3D viewer only fills the pane when it's the sole thing attached.
function V2Body({ issue, canEdit, onSaveFields, linkedOpts = {}, userInfo, onIssueUpdated, screenshot, onMarkupSave, actionBar, shotSaveRef, fullscreen, resolutionShot, resolutionInfo, onShowShot, review3D }) {
  const diff = issue.diffSummary || {};
  const commitSha = String(issue.resolution?.commitSha || issue.sourceModel?.commitSha || "").slice(0, 7);
  const modelFile = issue.sourceModel?.name
    ? `${issue.sourceModel.name}${(issue.sourceModel?.zipInfo?.paths?.length ? ` · ${issue.sourceModel.zipInfo.paths.length} file(s)` : "")}`
    : "3D model";
  return (
    <>
      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        padding: "14px 16px 14px 20px",
      }}>
        {screenshot ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
                SOURCE · WHERE ISSUE WAS RAISED
              </span>
              {commitSha && <Pill kind="navy">commit {commitSha}</Pill>}
            </div>
            {actionBar}
            <ScreenshotMarkupPane
              issue={issue} canEdit={canEdit} userInfo={userInfo}
              onSave={onMarkupSave} screenshot={screenshot} fill
              saveRef={shotSaveRef} fullscreen={fullscreen}
            />
            <Collapsible3D
              issue={issue} canEdit={canEdit} userInfo={userInfo}
              onIssueUpdated={onIssueUpdated}
              fileLabel={modelFile}
              review={review3D}
            />
          </>
        ) : (
          <>
            {/* 3D is the only source attached — render it expanded, as today */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
                {modelSourceLabel(issue)}
              </span>
              {commitSha && <Pill kind="navy">commit {commitSha}</Pill>}
            </div>
            {actionBar}
            <IssueModelCanvas
              issue={issue} canEdit={canEdit} userInfo={userInfo}
              onIssueUpdated={onIssueUpdated} fill
              review={review3D}
            />
          </>
        )}
      </div>

      {/* Side rail — diff stats + screenshot + checklist */}
      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        borderLeft: `1px solid ${T.line}`, background: "#fff",
        padding: "14px 16px", overflow: "auto",
      }}>
        <LinkedToBlock issue={issue} canEdit={canEdit && linkedOpts.canEditFields !== false} canPickContractor={linkedOpts.canPickContractor !== false} onSaveFields={onSaveFields} />
        <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 8 }}>
          DIFF SUMMARY
        </div>
        <div style={{
          padding: "10px 12px", background: T.bg,
          border: `1px solid ${T.line}`, borderRadius: 6,
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
        }}>
          {[
            { k: "Objects modified", v: diff.componentsModified ?? "—", fg: T.navyD },
            { k: "New clashes", v: diff.newClashes ?? "—", fg: T.added2 },
            { k: "Projected cost", v: projectedCostText(issue.projection, issue.estimatedHours), fg: T.amber },
            { k: "Commit", v: commitSha || "—", fg: T.navy },
          ].map((s) => (
            <div key={s.k}>
              <div style={{ fontSize: 15, fontWeight: 700, color: s.fg }}>{s.v}</div>
              <div style={{ fontSize: 10.5, color: T.muted, marginTop: 4, fontWeight: 500 }}>{s.k}</div>
            </div>
          ))}
        </div>

        <ResolutionShotBox
          shot={resolutionShot} fallback={screenshot} info={resolutionInfo} hours={issue?.hoursSpent}
          dark maxHeight={200} onOpen={onShowShot}
        />

        <ResolveChecklist
          hasScreenshot={!!(resolutionShot || screenshot)}
          hasTaggedUsers={((issue.responsibleUsers || issue.assignedTo || []).length) > 0}
          policy={issue.evidencePolicy}
          hasDocument={(issue.attachments || []).some((a) => ["pdf", "excel", "doc", "document"].includes(a?.kind) || /\.(pdf|xlsx?|xlsm|csv|docx?)(\?|$)/i.test(a?.url || a?.name || ""))}
          hasCommit={!!commitSha}
          needsCommit
        />
      </div>
    </>
  );
}

// ─── V3 body — Hybrid (2D sheet primary + collapsed 3D + linked meta) ────
function V3Body({ issue, canEdit, onSaveFields, linkedOpts = {}, userInfo, onSave, onIssueUpdated, screenshot, onMarkupSave, actionBar, sheetSaveRef, shotSaveRef, fullscreen, resolutionShot, resolutionInfo, onShowShot, review3D, surface }) {
  const sheet = issue.sourceDocument?.sheetNumber || "";
  const commitSha = String(issue.resolution?.commitSha || issue.sourceModel?.commitSha || "").slice(0, 7);
  // The sheet is the canvas unless a picture was picked (surface
  // "image") or there is no sheet at all.
  const showSheet = !!issue.sourceDocument?.url && !(surface === "image" && screenshot);
  const annotations = (issue.issueObjects || [])
    .filter((o) => o?.data?.note || o?.data?.text)
    .slice(0, 5);

  return (
    <>
      {/* Primary pane — 2D sheet fills (or the screenshot when no sheet is
          attached); 3D stays collapsed below it */}
      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        padding: "14px 12px 14px 20px", borderRight: `1px solid ${T.line}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
            {showSheet || !screenshot ? "2D SHEET" : "SOURCE · WHERE ISSUE WAS RAISED"}
          </span>
          {sheet && <Pill>{sheet}</Pill>}
          {commitSha && <Pill kind="added">commit {commitSha}</Pill>}
        </div>
        {actionBar}
        {showSheet || !screenshot ? (
          <SheetPane issue={issue} canEdit={canEdit} userInfo={userInfo} onSave={onSave} fill saveRef={sheetSaveRef} fullscreen={fullscreen} />
        ) : (
          <ScreenshotMarkupPane
            issue={issue} canEdit={canEdit} userInfo={userInfo}
            onSave={onMarkupSave} screenshot={screenshot} fill
            saveRef={shotSaveRef} fullscreen={fullscreen}
          />
        )}
        <Collapsible3D
          issue={issue} canEdit={canEdit} userInfo={userInfo}
          onIssueUpdated={onIssueUpdated}
          fileLabel={issue.sourceModel?.name || ""}
          review={review3D}
        />
      </div>

      {/* Linked-to / annotations / screenshot rail */}
      <div style={{
        display: "flex", flexDirection: "column", minHeight: 0, height: "100%",
        background: T.bg, borderRight: `1px solid ${T.line}`, overflow: "auto",
      }}>
        <LinkedToBlock issue={issue} bordered canEdit={canEdit && linkedOpts.canEditFields !== false} canPickContractor={linkedOpts.canPickContractor !== false} onSaveFields={onSaveFields} />

        {annotations.length > 0 && (
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.line}` }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 8 }}>
              ANNOTATIONS ({annotations.length})
            </div>
            {annotations.map((a, i) => (
              <div key={i} style={{
                display: "flex", gap: 6, padding: "5px 0",
                borderBottom: i < annotations.length - 1 ? `1px dashed ${T.line2}` : "none",
              }}>
                <span style={{
                  width: 22, height: 22, background: T.orange, color: "#fff",
                  borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700, flexShrink: 0,
                }}>#{i + 1}</span>
                <div style={{ flex: 1, fontSize: 11, color: T.text, lineHeight: 1.4 }}>
                  {a.data.note || a.data.text}
                </div>
                <Avatar user={a.createdBy || { name: "?" }} size={16} />
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: "14px 16px" }}>
          <ResolutionShotBox
            shot={resolutionShot} fallback={screenshot} info={resolutionInfo} hours={issue?.hoursSpent}
            dark maxHeight={180} onOpen={onShowShot}
          />

          <ResolveChecklist
            hasScreenshot={!!(resolutionShot || screenshot)}
            hasTaggedUsers={((issue.responsibleUsers || issue.assignedTo || []).length) > 0}
          policy={issue.evidencePolicy}
          hasDocument={(issue.attachments || []).some((a) => ["pdf", "excel", "doc", "document"].includes(a?.kind) || /\.(pdf|xlsx?|xlsm|csv|docx?)(\?|$)/i.test(a?.url || a?.name || ""))}
            hasCommit={!!commitSha}
            needsCommit
          />
        </div>
      </div>
    </>
  );
}
